import {
    DEFAULT_PICTIONARY_HINT_MODE,
    PICTIONARY_DEFAULT_REVEAL_DURATION_SECONDS,
    PICTIONARY_EFFORT_FEEDBACK_MESSAGE,
    PICTIONARY_LIVES_PER_ROUND,
    PICTIONARY_MAX_POINT_VALUE,
    PICTIONARY_MAX_SEGMENT_POINTS,
    PICTIONARY_MIN_POINT_VALUE,
    PARTICIPANT_AWAY_GRACE_MS,
    PICTIONARY_ROUND_DURATION_SECONDS,
    PICTIONARY_ROUND_TIMER_GRACE_START_SECONDS,
    PICTIONARY_REVEAL_DURATION_STEP_SECONDS,
    ROOM_CODE_MAX_LENGTH,
    buildPictionaryStemRevealSegments,
    clampPictionaryPointValue,
    getPictionaryDefaultNextPointValue,
    clampPictionaryRevealDurationSeconds,
    getPictionaryGuessPenaltyLives,
    getPictionaryRoundAutoSubmitAt,
    getPictionaryRoundTimerNumeral,
    getPictionaryStemRevealCount,
    isValidRoomCode,
    normalizeLooseGuess,
    normalizeRoomCode,
    type PictionaryCanvasPoint,
    type PictionaryCanvasSegmentMessage,
    type PictionaryFeedbackKind,
    type PictionaryHintMode,
    type PictionaryStrokeView,
    type RoomView
} from "../../shared/game";
import { renderActiveHostJoinUtility } from "./activeHostUtility";
import { createCapyShieldElement, setCapyShieldHue } from "./capyShield";
import { getLanguage, localizeRuntimeText, t } from "./i18n";
import { PictionaryAudio } from "./pictionaryAudio";
import { PictionaryTranslations } from "./pictionaryTranslations";
import { getPictionaryTranslationRankFraction, getLegacyPictionaryTranslationRechargeMs } from "../../shared/pictionaryTranslations";
import {
    assignOrderedPlayerRosterPlacements,
    getPlayerRosterDisplayName,
    getPlayerRosterListClass,
    getPlayerRosterPlacementClass,
    getPlayerRenamePendingClass,
    renderPlayerRosterActionsMenu,
    syncPlayerRosterLayout
} from "./playerRoster";
import {
    downloadPlayerHistory,
    renderPlayerHistoryHeaderMenu
} from "./playerHistoryDownload";
import { SITE_TUNING } from "./siteTuning";
import { createVocabularyReviewAppearance } from "./vocabularyReviewAppearance";
import { centerReviewWord, observeReviewGeometry, renderReviewList, renderReviewNavigation, replaceReviewMarkup, reviewIndexForKey } from "./vocabularyReviewList";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";
import { GameSurfaceMotion } from "./gameSurfaceMotion";

type RoleHint = "host" | "player" | "controller" | null;
type RenderRole = "host" | "player" | "controller" | "host-controller";

type PictionaryViewOptions = {
    getRoleHint: () => RoleHint;
    onGuess: (guess: string) => void;
    onNextRound: (
        entryIndex: number,
        pointValue: number,
        revealDurationSeconds: number,
        hintMode: PictionaryHintMode
    ) => void;
    onEndRound: () => void;
    onDraw: (payload: { strokeId: string; begin: boolean; end?: boolean; points: PictionaryCanvasPoint[] }) => void;
    onUndo: () => void;
    onClear: () => void;
    onControllerRoomChange: (roomCode: string) => void;
    onRenameParticipant: (sessionId: string, anchorSelector: string) => void;
    onPromptRenameParticipant: (sessionId: string) => void;
    onKickParticipant: (sessionId: string) => void;
    onAdjustScore: (sessionId: string, delta: number) => void;
    onPreviewWorth: (pointValue: number, manual: boolean) => void;
    onVocabularyReview: (open: boolean, index: number) => void;
    onTranslationUsage?: (payload: import("../../shared/game").PictionaryTranslationUsageMessage["payload"]) => boolean;
};

const BRUSH_WIDTH = SITE_TUNING.pictionary.drawing.brushWidthPx;
const MIN_POINT_DISTANCE_PX = SITE_TUNING.pictionary.drawing.minPointDistancePx;
const MAX_POINT_GAP_PX = SITE_TUNING.pictionary.drawing.maxPointGapPx;
const MAX_STROKE_JUMP_PX = SITE_TUNING.pictionary.drawing.maxStrokeJumpPx;
const SMOOTHING_WEIGHT = SITE_TUNING.pictionary.drawing.smoothingWeight;
const DRAW_SAFE_BOTTOM_INSET_MIN_PX = SITE_TUNING.pictionary.drawing.safeBottomInsetMinPx;
const DRAW_SAFE_BOTTOM_INSET_MAX_PX = SITE_TUNING.pictionary.drawing.safeBottomInsetMaxPx;
const DRAW_SAFE_BOTTOM_INSET_RATIO = SITE_TUNING.pictionary.drawing.safeBottomInsetRatio;
const IDLE_PARTICLE_COUNT = SITE_TUNING.pictionary.idle.particleCount;

type NormalizedCanvasPoint = PictionaryCanvasPoint & {
    gapBefore?: boolean;
};

const createStrokeId = (): string => {
    const browserCrypto = globalThis.crypto;

    if (typeof browserCrypto?.randomUUID === "function") {
        return browserCrypto.randomUUID().replace(/-/g, "");
    }

    if (typeof browserCrypto?.getRandomValues === "function") {
        const bytes = new Uint8Array(16);
        browserCrypto.getRandomValues(bytes);
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }

    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
};
const ROUND_COMPLETE_PROMPT_DELAY_MS = SITE_TUNING.pictionary.idle.roundCompletePromptDelayMs;
const ROUND_COMPLETE_PROMPT_FADE_MS = SITE_TUNING.pictionary.idle.roundCompletePromptFadeMs;
const MOBILE_GUESS_KEYBOARD_MIN_INSET_PX = SITE_TUNING.pictionary.layout.mobileKeyboardMinInsetPx;
const KEYBOARD_LIFT_MAX_INSET_PX = SITE_TUNING.pictionary.layout.keyboardLiftMaxInsetPx;
const KEYBOARD_LIFT_INSET_RATIO = SITE_TUNING.pictionary.layout.keyboardLiftInsetRatio;
const KEYBOARD_SCROLL_RESTORE_DELAY_MS = SITE_TUNING.pictionary.layout.keyboardScrollRestoreDelayMs;
const SHORT_KEYBOARD_VIEWPORT_MAX_HEIGHT_PX = SITE_TUNING.pictionary.layout.shortKeyboardViewportMaxHeightPx;
const PICTIONARY_CAPY_INITIAL_PEEK_DELAY_MS = SITE_TUNING.pictionary.mascot.initialPeekDelayMs;
const PICTIONARY_CAPY_ROUND_PEEK_DELAY_MS = SITE_TUNING.pictionary.mascot.roundPeekDelayMs;
const PICTIONARY_CAPY_GUARD_RETRY_MS = SITE_TUNING.pictionary.mascot.guardRetryMs;
const PICTIONARY_CAPY_VISIBLE_MS = SITE_TUNING.pictionary.mascot.visibleMs;
const PICTIONARY_CAPY_RETREAT_MS = SITE_TUNING.pictionary.mascot.retreatMs;
const PICTIONARY_CAPY_TAP_REACTION_MS = SITE_TUNING.pictionary.mascot.tapReactionMs;
const PICTIONARY_CAPY_TAP_RELOCATE_DELAY_MS = SITE_TUNING.pictionary.mascot.tapRelocateDelayMs;
const PICTIONARY_CAPY_COOLDOWN_MS = SITE_TUNING.pictionary.mascot.cooldownMs;
const PICTIONARY_CAPY_MAX_APPEARANCES = SITE_TUNING.pictionary.mascot.maxAppearancesPerContext;

const PICTIONARY_CAPY_ANCHORS = ["left-upper", "right-middle", "right-upper", "left-middle"] as const;
type PictionaryCapyAnchor = (typeof PICTIONARY_CAPY_ANCHORS)[number];

type GuessInputSnapshot = {
    wasFocused: boolean;
    selectionStart: number | null;
    selectionEnd: number | null;
};

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const isPictionaryDebugEnabled = (): boolean => {
    try {
        return localStorage.getItem("ziarcadia:pictionary-debug") !== "0";
    } catch {
        return true;
    }
};

const comparePictionaryDisplayEntries = <Entry extends { english: string; korean?: string; index?: number }>(
    left: Entry,
    right: Entry
): number => {
    const englishComparison = left.english.localeCompare(right.english, undefined, {
        numeric: true,
        sensitivity: "base"
    });

    if (englishComparison !== 0) {
        return englishComparison;
    }

    const koreanComparison = (left.korean ?? "").localeCompare(right.korean ?? "", undefined, {
        numeric: true,
        sensitivity: "base"
    });

    if (koreanComparison !== 0) {
        return koreanComparison;
    }

    return (left.index ?? 0) - (right.index ?? 0);
};

const formatCountdownSeconds = (targetTime: number | null, now = Date.now()): string => {
    if (!targetTime) {
        return "";
    }

    const remainingMs = Math.max(0, targetTime - now);
    return remainingMs > 0 ? `${Math.max(1, Math.ceil(remainingMs / 1000))}s` : "";
};

const ROUND_TIMER_NUMERAL_LINGER_MS = 2200;

const cloneStrokes = (strokes: PictionaryStrokeView[]): PictionaryStrokeView[] =>
    strokes.map((stroke) => ({
        id: stroke.id,
        points: stroke.points.map((point) => ({ ...point }))
    }));

const localizeStatus = (message: string | null | undefined): string =>
    localizeRuntimeText(message ?? "");

const getPictionaryBreakthroughFeedbackPoints = (message: string | null | undefined): number => {
    const match = message?.match(/(?:Breakthrough|Lucky break) \+(\d+)!/);
    const points = match ? Number.parseInt(match[1] ?? "", 10) : 0;

    return Number.isFinite(points) ? Math.max(0, points) : 0;
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const easeInOutCubic = (value: number): number => {
    const clampedValue = clamp01(value);

    return clampedValue < 0.5
        ? 4 * clampedValue * clampedValue * clampedValue
        : 1 - Math.pow(-2 * clampedValue + 2, 3) / 2;
};

const STEM_TRAILING_PUNCTUATION_PATTERN = /^\s*[?!.,;:]+["')\]]*$/;

const renderStaticStemSegment = (text: string): string =>
    text
        .split(/(___+)/g)
        .map((part) =>
            /^___+$/.test(part)
                ? `<span class="pictionary-target-blank">${escapeHtml(part)}</span>`
                : escapeHtml(part)
        )
        .join("");

const renderStaticStemSegmentWithPunctuationGlue = (text: string): string =>
    STEM_TRAILING_PUNCTUATION_PATTERN.test(text)
        ? `&#8288;${renderStaticStemSegment(text.replace(/^\s+/, ""))}`
        : renderStaticStemSegment(text);

const renderRoundAnswerMarkup = (pictionary: NonNullable<RoomView["pictionary"]>): string =>
    pictionary.roundComplete && pictionary.currentWordEnglish
        ? `<span class="pictionary-round-answer-panel"><span class="pictionary-round-answer">${escapeHtml(
              pictionary.currentWordEnglish
          )}</span>${!pictionary.noTranslations && pictionary.currentWordKorean?.trim()
              ? `<span class="pictionary-round-answer-translation" lang="ko">${escapeHtml(pictionary.currentWordKorean)}</span>`
              : ""}</span>`
        : "";

const clampTryCount = (count: number): number =>
    Number.isFinite(count) ? Math.max(0, Math.min(PICTIONARY_LIVES_PER_ROUND, Math.trunc(count))) : 0;

const clampRiskedTryCount = (remainingCount: number, riskCount: number): number =>
    Number.isFinite(riskCount) ? Math.max(0, Math.min(clampTryCount(remainingCount), Math.trunc(riskCount))) : 0;

const PICTIONARY_TRY_FALL_ANIMATION_MS = 980;
const PICTIONARY_TRY_FALL_HOLD_MS = 160;
const PICTIONARY_TEAM_SCORE_POPUP_MS = 1650;

type TryLossAnimationState = {
    fromLives: number;
    toLives: number;
    lostCount: number;
    expiresAt: number;
};

type TeamScorePopupState = {
    points: number;
    sequence: number;
    expiresAt: number;
};

type PictionaryAwardBreakdownItem = {
    label: string;
    points: number;
    tone: "case" | "speed" | "breakthrough" | "effort" | "never-quit";
};

type PictionaryCelebrationDetails = {
    headline: string;
    className: string;
    breakdownItems: PictionaryAwardBreakdownItem[];
};

type PictionaryViewerAward = NonNullable<NonNullable<RoomView["pictionary"]>["viewer"]>["lastAward"];

const normalizeAwardPoints = (points: number | undefined): number =>
    typeof points === "number" && Number.isFinite(points) ? Math.max(0, Math.trunc(points)) : 0;

const getPictionaryAwardBreakdownItems = (
    award: PictionaryViewerAward,
    fallbackBreakthroughPoints: number,
    fallbackSpeedBonusPoints: number
): PictionaryAwardBreakdownItem[] => {
    if (award) {
        return [
            normalizeAwardPoints(award.casePoints) > 0
                ? {
                      label: t("pictionaryAwardCase"),
                      points: normalizeAwardPoints(award.casePoints),
                      tone: "case" as const
                  }
                : null,
            normalizeAwardPoints(award.zeroHeartSolvePoints) > 0
                ? {
                      label: t("pictionaryAwardNeverQuit"),
                      points: normalizeAwardPoints(award.zeroHeartSolvePoints),
                      tone: "never-quit" as const
                  }
                : null,
            normalizeAwardPoints(award.speedBonusPoints) > 0
                ? {
                      label: t("pictionaryAwardSpeed"),
                      points: normalizeAwardPoints(award.speedBonusPoints),
                      tone: "speed" as const
                  }
                : null,
            normalizeAwardPoints(award.breakthroughBonusPoints) > 0
                ? {
                      label: t("pictionaryAwardBreakthrough"),
                      points: normalizeAwardPoints(award.breakthroughBonusPoints),
                      tone: "breakthrough" as const
                  }
                : null,
            normalizeAwardPoints(award.effortPoints) > 0
                ? {
                      label: t("pictionaryAwardEffort"),
                      points: normalizeAwardPoints(award.effortPoints),
                      tone: "effort" as const
                  }
                : null
        ].filter((item): item is PictionaryAwardBreakdownItem => item !== null);
    }

    const fallbackItems: Array<PictionaryAwardBreakdownItem | null> = [
        fallbackSpeedBonusPoints > 0
            ? {
                  label: t("pictionaryAwardSpeed"),
                  points: fallbackSpeedBonusPoints,
                  tone: "speed" as const
              }
            : null,
        fallbackBreakthroughPoints > 0
            ? {
                  label: t("pictionaryAwardBreakthrough"),
                  points: fallbackBreakthroughPoints,
                  tone: "breakthrough" as const
              }
            : null
    ];

    return fallbackItems.filter((item): item is PictionaryAwardBreakdownItem => item !== null);
};

const getPictionaryCelebrationDetails = (
    award: PictionaryViewerAward,
    fallbackBreakthroughPoints: number,
    fallbackSpeedBonusPoints: number,
    fallbackMessage: string | null | undefined
): PictionaryCelebrationDetails => {
    const breakdownItems = getPictionaryAwardBreakdownItems(
        award,
        fallbackBreakthroughPoints,
        fallbackSpeedBonusPoints
    );
    const hasSpeed = breakdownItems.some((item) => item.tone === "speed");
    const hasBreakthrough = breakdownItems.some((item) => item.tone === "breakthrough");
    const hasEffort = breakdownItems.some((item) => item.tone === "effort");
    const hasNeverQuit = breakdownItems.some((item) => item.tone === "never-quit");
    const headline = hasBreakthrough
        ? t("pictionaryCelebrationBreakthrough")
        : hasSpeed
          ? t("pictionaryCelebrationSpeed")
          : hasEffort || fallbackMessage === PICTIONARY_EFFORT_FEEDBACK_MESSAGE
            ? t("pictionaryCelebrationEffort")
            : hasNeverQuit
              ? t("pictionaryCelebrationNeverQuit")
              : t("pictionaryCelebrationSolved");
    const className = hasBreakthrough
        ? "is-breakthrough-bonus"
        : hasSpeed
          ? "is-speed-bonus"
          : hasEffort
            ? "is-effort-bonus"
            : hasNeverQuit
              ? "is-never-quit-bonus"
              : "";

    return { headline, className, breakdownItems };
};

const buildTryLossAnimationMarkup = (animation: TryLossAnimationState, tokenIndex: number): string => {
    const fallOrder = tokenIndex - animation.toLives;
    const centeredOffset = fallOrder - (animation.lostCount - 1) / 2;
    const fallOffset = Math.round(centeredOffset * 3);

    return `<span class="pictionary-life-fall" style="--pictionary-fall-x: ${fallOffset}px;" aria-hidden="true"><span class="pictionary-life-icon"></span></span>`;
};

const buildLivesMarkup = (
    count: number,
    riskCount = 0,
    lossAnimation: TryLossAnimationState | null = null
): string => {
    const safeCount = clampTryCount(count);
    const safeRiskCount = clampRiskedTryCount(safeCount, riskCount);
    const firstRiskedIndex = Math.max(0, safeCount - safeRiskCount);
    const fallStartIndex = lossAnimation ? Math.max(0, lossAnimation.toLives) : -1;
    const fallEndIndex = lossAnimation ? Math.min(PICTIONARY_LIVES_PER_ROUND, lossAnimation.fromLives) : -1;

    return Array.from({ length: PICTIONARY_LIVES_PER_ROUND }, (_, index) => {
        const isFilled = index < safeCount;
        const isRisked = isFilled && index >= firstRiskedIndex;
        const isFalling = Boolean(lossAnimation && index >= fallStartIndex && index < fallEndIndex);
        return `<span class="pictionary-life-token ${isFilled ? "" : "is-empty"}${
            isRisked ? " is-risked" : ""
        }${isFalling ? " is-falling" : ""}" aria-hidden="true"><span class="pictionary-life-icon"></span>${
            isFalling && lossAnimation ? buildTryLossAnimationMarkup(lossAnimation, index) : ""
        }</span>`;
    }).join("");
};

const getFeedbackToneClass = (kind: PictionaryFeedbackKind | null | undefined): string => {
    switch (kind) {
        case "wrong":
            return "is-wrong";
        case "misspelling":
            return "is-misspelling";
        case "correct":
            return "is-correct";
        default:
            return "";
    }
};

const isDefaultGuessPrompt = (message: string): boolean => message === "Guess the drawing.";

const UNDO_ICON = renderZiarcadiaIcon("undo");
const CLEAR_CANVAS_ICON = renderZiarcadiaIcon("clear");
const MAGNIFIER_ICON = renderZiarcadiaIcon("magnifier", {
    className: "pictionary-metric-icon"
});
const GUESS_MAGNIFIER_ICON = renderZiarcadiaIcon("magnifier", {
    className: "pictionary-guess-search-icon"
});
const PRIVATE_WORD_ICON = renderZiarcadiaIcon("eyeOff", {
    className: "pictionary-controller-private-icon"
});
const MAX_PICTIONARY_GUESS_SUGGESTIONS = 3;
type PictionaryGuessSuggestion = {
    english: string;
    korean?: string;
};

const getPictionaryGuessSuggestions = (
    wordBank: readonly PictionaryGuessSuggestion[],
    draftGuess: string
): PictionaryGuessSuggestion[] => {
    const normalizedDraft = normalizeLooseGuess(draftGuess);

    if (!normalizedDraft) {
        return [];
    }

    const seenNormalizedWords = new Set<string>();
    const suggestions: PictionaryGuessSuggestion[] = [];

    const rankedEntries = [...wordBank]
        .map((entry) => {
            const normalizedEnglish = normalizeLooseGuess(entry.english);
            const englishWords = normalizedEnglish.split(" ").filter(Boolean);
            const startsAtBeginning = normalizedEnglish.startsWith(normalizedDraft);
            const startsAtWord = englishWords.some((word) => word.startsWith(normalizedDraft));

            return {
                entry,
                normalizedEnglish,
                rank: startsAtBeginning ? 0 : startsAtWord ? 1 : 2
            };
        })
        .filter(({ rank }) => rank < 2)
        .sort((left, right) => left.rank - right.rank || comparePictionaryDisplayEntries(left.entry, right.entry));

    for (const { entry, normalizedEnglish } of rankedEntries) {
        if (
            !normalizedEnglish ||
            seenNormalizedWords.has(normalizedEnglish)
        ) {
            continue;
        }

        seenNormalizedWords.add(normalizedEnglish);
        suggestions.push(entry);

        if (suggestions.length >= MAX_PICTIONARY_GUESS_SUGGESTIONS) {
            break;
        }
    }

    return suggestions;
};

const getPictionaryGuessSuggestionKey = (suggestions: readonly PictionaryGuessSuggestion[]): string =>
    suggestions.map((suggestion) => normalizeLooseGuess(suggestion.english)).join("|");

const renderPictionaryGuessSuggestionButtons = (suggestions: readonly PictionaryGuessSuggestion[]): string =>
    suggestions
        .map(
            (suggestion) => `
                <button
                    class="pictionary-guess-suggestion-button"
                    type="button"
                    data-pictionary-guess-suggestion="${escapeHtml(suggestion.english)}"
                ><span>${escapeHtml(suggestion.english)}</span></button>
            `
        )
        .join("");

const buildPictionaryRosterRenameButtonId = (sessionId: string): string =>
    `pictionary-roster-rename-${sessionId.replace(/[^a-z0-9_-]/gi, "_")}`;

const buildPictionaryRosterActionsKey = (sessionId: string): string => `pictionary:${sessionId}`;

const buildPictionaryRosterActionsTriggerId = (sessionId: string): string =>
    `pictionary-roster-actions-${sessionId.replace(/[^a-z0-9_-]/gi, "_")}`;

const formatLivesRemaining = (count: number): string =>
    count === 1 ? t("livesRemainingOne") : t("livesRemaining", { count });

type PictionaryCardMetricsOptions = {
    adjustableSessionId?: string;
    adjustOpen?: boolean;
};

const renderPictionaryCardMetricsMarkup = (
    livesRemaining: number,
    score: number,
    className: string,
    options: PictionaryCardMetricsOptions = {}
): string => {
    const safeLivesRemaining = Math.max(0, Math.min(PICTIONARY_LIVES_PER_ROUND, Math.trunc(livesRemaining)));
    const safeScore = Math.max(0, Math.trunc(score));
    const exhaustedTriesClass = safeLivesRemaining <= 0 ? " is-exhausted" : "";
    const canAdjust = Boolean(options.adjustableSessionId);
    const pointsLabel = `${t("points")}: ${safeScore}`;
    const scoreMarkup = canAdjust
        ? `
            <button
                class="pictionary-card-metric pictionary-card-metric--points pictionary-score-adjust-trigger"
                type="button"
                data-action="toggle-score-adjust"
                data-session-id="${escapeHtml(options.adjustableSessionId ?? "")}"
                aria-label="${escapeHtml(`${t("adjustPoints")}: ${safeScore}`)}"
                aria-expanded="${options.adjustOpen ? "true" : "false"}"
                title="${escapeHtml(t("adjustPoints"))}"
            >
                <strong>${escapeHtml(String(safeScore))}</strong>
            </button>
            <span class="pictionary-score-adjust-popover${options.adjustOpen ? " is-open" : ""}" role="group" aria-label="${escapeHtml(
                t("adjustPoints")
            )}">
                <button
                    class="pictionary-score-adjust-button"
                    type="button"
                    data-action="adjust-score"
                    data-score-delta="-1"
                    data-session-id="${escapeHtml(options.adjustableSessionId ?? "")}"
                    aria-label="${escapeHtml(t("subtractPoint"))}"
                    title="${escapeHtml(t("subtractPoint"))}"
                >-</button>
                <button
                    class="pictionary-score-adjust-button"
                    type="button"
                    data-action="adjust-score"
                    data-score-delta="1"
                    data-session-id="${escapeHtml(options.adjustableSessionId ?? "")}"
                    aria-label="${escapeHtml(t("addPoint"))}"
                    title="${escapeHtml(t("addPoint"))}"
                >+</button>
            </span>
        `
        : `
            <span
                class="pictionary-card-metric pictionary-card-metric--points"
                aria-label="${escapeHtml(pointsLabel)}"
                title="${escapeHtml(pointsLabel)}"
            >
                <strong>${escapeHtml(String(safeScore))}</strong>
            </span>
        `;

    return `
        <span class="${className} pictionary-card-metrics${canAdjust ? " is-score-adjustable" : ""}">
            <span
                class="pictionary-card-metric pictionary-card-metric--tries${exhaustedTriesClass}"
                aria-label="${escapeHtml(formatLivesRemaining(safeLivesRemaining))}"
                title="${escapeHtml(formatLivesRemaining(safeLivesRemaining))}"
            >
                ${MAGNIFIER_ICON}
                <strong>${escapeHtml(String(safeLivesRemaining))}</strong>
            </span>
            ${scoreMarkup}
        </span>
    `;
};

const pointsEqual = (left: PictionaryCanvasPoint, right: PictionaryCanvasPoint): boolean =>
    Math.abs(left.x - right.x) < 0.0005 && Math.abs(left.y - right.y) < 0.0005;

const containsPointRun = (
    points: readonly PictionaryCanvasPoint[],
    candidateRun: readonly PictionaryCanvasPoint[]
): boolean => {
    if (candidateRun.length === 0) {
        return true;
    }

    if (candidateRun.length > points.length) {
        return false;
    }

    for (let startIndex = 0; startIndex <= points.length - candidateRun.length; startIndex += 1) {
        if (candidateRun.every((point, index) => pointsEqual(points[startIndex + index]!, point))) {
            return true;
        }
    }

    return false;
};

const getPointTailOverlapCount = (
    points: readonly PictionaryCanvasPoint[],
    candidateRun: readonly PictionaryCanvasPoint[]
): number => {
    const maxOverlap = Math.min(points.length, candidateRun.length);

    for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
        const startIndex = points.length - overlap;

        if (
            candidateRun
                .slice(0, overlap)
                .every((point, index) => pointsEqual(points[startIndex + index]!, point))
        ) {
            return overlap;
        }
    }

    return 0;
};

const formatPts = (value: number): string => `${value} pts`;
const formatPointCount = (value: number): string =>
    value === 1 ? t("pointCountOne") : t("pointCountMany", { count: value });
const formatControllerRoomCode = (value: string): string =>
    normalizeRoomCode(value).slice(0, ROOM_CODE_MAX_LENGTH);
const getDefaultSelectedPointValue = (pictionary: NonNullable<RoomView["pictionary"]>): number =>
    clampPictionaryPointValue(
        pictionary.nextWorthManual
            ? pictionary.nextWorth
            : pictionary.roundActive && !pictionary.roundComplete
            ? pictionary.pointValue
            : getPictionaryDefaultNextPointValue(pictionary.roundNumber)
    );
const getPictionaryDisplayWorth = (pictionary: NonNullable<RoomView["pictionary"]>): number =>
    pictionary.roundActive && !pictionary.roundComplete ? pictionary.currentWorth : pictionary.nextWorth;
const getPictionaryWaitingPrompt = (pictionary: NonNullable<RoomView["pictionary"]>): string =>
    pictionary.roundActive
        ? t("hostMustChooseNextWordWithPoints", { points: formatPointCount(pictionary.nextWorth) })
        : t("waitingForWordWithPoints", { points: formatPointCount(pictionary.nextWorth) });

export class PictionaryView {
    private readonly root: HTMLElement;
    private readonly reviewAppearance: ReturnType<typeof createVocabularyReviewAppearance>;
    private readonly stopObservingReview: () => void;
    private readonly options: PictionaryViewOptions;
    private readonly audio = new PictionaryAudio();
    private readonly shell: HTMLElement;
    private readonly stageStatus: HTMLElement;
    private readonly pointsLabel: HTMLElement;
    private readonly pointsValue: HTMLElement;
    private readonly stemValue: HTMLElement;
    private readonly roundValue: HTMLElement;
    private readonly roundTimer: HTMLElement;
    private readonly capyPeekButton: HTMLButtonElement;
    private readonly capyPeekShield: HTMLElement;
    private readonly viewerNameChip: HTMLElement;
    private readonly playerCelebration: HTMLElement;
    private readonly worthBurst: HTMLElement;
    private readonly sideCard: HTMLElement;
    private readonly canvasShell: HTMLElement;
    private readonly canvas: HTMLCanvasElement;
    private readonly roundAnswerValue: HTMLElement;
    private readonly hostTools: HTMLElement;
    private readonly consoleSlot: HTMLElement;
    private readonly playerBoardSlot: HTMLElement;
    private readonly roleSlot: HTMLElement;
    private readonly hostUtilitySlot: HTMLElement;
    private readonly rosterSlot: HTMLElement;
    private readonly scoreboardSlot: HTMLElement;
    private readonly wordBankSlot: HTMLElement;
    private readonly resizeObserver: ResizeObserver | null;
    private readonly countdownTimer: number;
    private currentRoom: RoomView | null = null;
    private strokes: PictionaryStrokeView[] = [];
    private draftGuess = "";
    private selectedEntryIndex: number | null = null;
    private selectedPointValue = PICTIONARY_MIN_POINT_VALUE;
    private selectedRevealDurationSeconds = PICTIONARY_DEFAULT_REVEAL_DURATION_SECONDS;
    private selectedHintMode: PictionaryHintMode = DEFAULT_PICTIONARY_HINT_MODE;
    private currentRole: RenderRole = "player";
    private boardOpen = false;
    private wordBankOpen = false;
    private readonly translations = new PictionaryTranslations({
        getItem: (key) => window.sessionStorage.getItem(key),
        setItem: (key, value) => window.sessionStorage.setItem(key, value)
    });
    private translationBoundaryTimer: number | null = null;
    private translationBoundaryAt: number | null = null;
    private lastReviewPosition = "";
    private controllerSelectionOpen = true;
    private selectionDirty = false;
    private pointValueDirty = false;
    private lastLockedWordIndex: number | null = null;
    private currentRevealRoundKey = "";
    private currentStemMarkup = "";
    private currentAnswerMarkup = "";
    private caseMotionKey = "";
    private readonly caseMotion = new GameSurfaceMotion("picture-detectives");
    private readonly clueMotion = new GameSurfaceMotion("picture-detectives");
    private lastRevealCount = 0;
    private latestRevealIndices = new Set<number>();
    private latestRevealPulseUntil = 0;
    private roundTimerCountdownNumeral: number | null = null;
    private roundTimerCountdownPulse = "";
    private roundTimerCountdownVisibleUntil = 0;
    private roundTimerCountdownRoundKey = "";
    private lastRoundTimerCountdownSoundKey = "";
    private autoEndedRoundKey = "";
    private activeStrokeId: string | null = null;
    private activeDrawingPointerId: number | null = null;
    private queuedStrokePoints: PictionaryCanvasPoint[] = [];
    private pointerIsDown = false;
    private flushFrameId: number | null = null;
    private idleAnimationFrameId: number | null = null;
    private canvasRedrawFrameId: number | null = null;
    private strokeSignature = "";
    private roleMarkupCache = "";
    private consoleMarkupCache = "";
    private consoleRenderKey = "";
    private hostUtilityMarkupCache = "";
    private rosterMarkupCache = "";
    private openRosterActionsKey: string | null = null;
    private scoreAdjustSessionId: string | null = null;
    private playerHistoryMenuOpen = false;
    private rosterDebugSignature = "";
    private rosterLayoutFrameId: number | null = null;
    private playerBoardMarkupCache = "";
    private scoreboardMarkupCache = "";
    private wordBankMarkupCache = "";
    private lastViewerScore: number | null = null;
    private lastObservedNextWorth: number | null = null;
    private celebrationTimerId: number | null = null;
    private worthBurstTimerId: number | null = null;
    private audioStateInitialized = false;
    private lastWordStartSoundKey = "";
    private lastWordFinishedSoundKey = "";
    private roundCompleteVisualKey = "";
    private roundCompleteVisualStartedAt: number | null = null;
    private serverClockOffsetMs = 0;
    private teamScoreBySessionId = new Map<string, number>();
    private teamScorePopupSequence = 0;
    private teamScorePopupBySessionId = new Map<string, TeamScorePopupState>();
    private previousViewerRoundKey = "";
    private previousViewerLivesRemaining: number | null = null;
    private tryLossAnimation: TryLossAnimationState | null = null;
    private guessInputDocked = false;
    private guessInputScrollRestoreTimerId: number | null = null;
    private capyPeekScheduleTimerId: number | null = null;
    private capyPeekRetreatTimerId: number | null = null;
    private capyPeekTransitionTimerId: number | null = null;
    private capyPeekContextKey = "";
    private capyPeekAppearances = 0;
    private capyPeekAnchorIndex = 0;

    constructor(root: HTMLElement, options: PictionaryViewOptions) {
        this.root = root;
        this.options = options;
        this.reviewAppearance = createVocabularyReviewAppearance(root, "paper");
        this.stopObservingReview = observeReviewGeometry(root, () => {
            const round = this.currentRoom?.pictionary;
            return this.currentRole !== "player" && round?.vocabularyReview?.open && (!round.roundActive || round.roundComplete) ? round.vocabularyReview.index : null;
        });
        this.root.classList.add("pictionary-view-root");
        this.root.innerHTML = `
            <div class="pictionary-shell">
                <section class="card pictionary-stage-card">
                    <div class="pictionary-canvas-shell">
                        <canvas class="pictionary-canvas" aria-label="${escapeHtml(t("pictureDetectives"))}"></canvas>
                        <button
                            class="pictionary-capy-peek"
                            type="button"
                            data-pictionary-capy-peek
                            aria-label="${escapeHtml(t("capybaraDetective"))}"
                            hidden
                        ></button>
                        <div class="pictionary-stage-head">
                            <div class="pictionary-head-chip pictionary-head-chip-left">
                                <span class="pictionary-head-label">${escapeHtml(t("round"))}</span>
                                <strong class="pictionary-round-value">0</strong>
                                <div class="pictionary-round-timer" aria-hidden="true" hidden></div>
                            </div>
                            <div class="pictionary-stem-banner"></div>
                            <div class="pictionary-head-chip pictionary-head-chip-right">
                                <span class="pictionary-head-label">${escapeHtml(t("worth"))}</span>
                                <strong class="pictionary-points-value">1</strong>
                            </div>
                            <div
                                class="pictionary-round-answer-slot"
                                data-pictionary-round-answer
                                hidden
                            ></div>
                        </div>
                        <div
                            class="pictionary-stage-viewer-chip"
                            hidden
                        ></div>
                        <div
                            class="pictionary-player-celebration"
                            data-pictionary-player-celebration
                            aria-live="polite"
                            hidden
                        ></div>
                        <div
                            class="pictionary-worth-burst"
                            data-pictionary-worth-burst
                            hidden
                        ></div>
                        <div class="pictionary-stage-status"></div>
                        <div class="pictionary-host-tools" hidden>
                            <button
                                class="secondary-button pictionary-host-tool-button"
                                type="button"
                                data-action="undo-stroke"
                                aria-label="${escapeHtml(t("undo"))}"
                                title="${escapeHtml(t("undo"))}"
                            >${UNDO_ICON}</button>
                            <button
                                class="secondary-button pictionary-host-tool-button"
                                type="button"
                                data-action="clear-canvas"
                                aria-label="${escapeHtml(t("clearCanvas"))}"
                                title="${escapeHtml(t("clearCanvas"))}"
                            >${CLEAR_CANVAS_ICON}</button>
                        </div>
                        <section class="pictionary-scoreboard-panel" data-pictionary-scoreboard-slot hidden></section>
                        <div class="pictionary-stage-console" data-pictionary-console-slot></div>
                    </div>
                </section>
                <section class="card pictionary-player-board-card" data-pictionary-player-board-slot hidden></section>
                <aside class="pictionary-side-card">
                    <section
                        class="card active-host-utility-card pictionary-host-utility-card"
                        data-pictionary-host-utility-slot
                        hidden
                    ></section>
                    <section class="roster-card pictionary-roster-card" data-pictionary-roster-slot hidden></section>
                    <section class="card pictionary-role-card" data-pictionary-role-slot></section>
                </aside>
                <section class="pictionary-word-bank-panel" data-pictionary-word-bank-slot hidden></section>
            </div>
        `;
        this.shell = this.root.querySelector<HTMLElement>(".pictionary-shell")!;
        this.stageStatus = this.root.querySelector<HTMLElement>(".pictionary-stage-status")!;
        this.pointsLabel = this.root.querySelector<HTMLElement>(".pictionary-head-chip-right .pictionary-head-label")!;
        this.pointsValue = this.root.querySelector<HTMLElement>(".pictionary-points-value")!;
        this.stemValue = this.root.querySelector<HTMLElement>(".pictionary-stem-banner")!;
        this.roundValue = this.root.querySelector<HTMLElement>(".pictionary-round-value")!;
        this.roundTimer = this.root.querySelector<HTMLElement>(".pictionary-round-timer")!;
        this.capyPeekButton = this.root.querySelector<HTMLButtonElement>("[data-pictionary-capy-peek]")!;
        this.capyPeekShield = createCapyShieldElement({ className: "pictionary-capy-shield" });
        this.capyPeekButton.append(this.capyPeekShield);
        this.capyPeekButton.style.setProperty(
            "--pictionary-capy-size",
            `${SITE_TUNING.pictionary.mascot.sizePx}px`
        );
        this.capyPeekButton.style.setProperty(
            "--pictionary-capy-size-compact",
            `${SITE_TUNING.pictionary.mascot.compactSizePx}px`
        );
        this.viewerNameChip = this.root.querySelector<HTMLElement>(".pictionary-stage-viewer-chip")!;
        this.playerCelebration = this.root.querySelector<HTMLElement>("[data-pictionary-player-celebration]")!;
        this.worthBurst = this.root.querySelector<HTMLElement>("[data-pictionary-worth-burst]")!;
        this.sideCard = this.root.querySelector<HTMLElement>(".pictionary-side-card")!;
        this.canvasShell = this.root.querySelector<HTMLElement>(".pictionary-canvas-shell")!;
        this.canvas = this.root.querySelector<HTMLCanvasElement>(".pictionary-canvas")!;
        this.roundAnswerValue = this.root.querySelector<HTMLElement>("[data-pictionary-round-answer]")!;
        this.hostTools = this.root.querySelector<HTMLElement>(".pictionary-host-tools")!;
        this.consoleSlot = this.root.querySelector<HTMLElement>("[data-pictionary-console-slot]")!;
        this.playerBoardSlot = this.root.querySelector<HTMLElement>("[data-pictionary-player-board-slot]")!;
        this.roleSlot = this.root.querySelector<HTMLElement>("[data-pictionary-role-slot]")!;
        this.hostUtilitySlot = this.root.querySelector<HTMLElement>("[data-pictionary-host-utility-slot]")!;
        this.rosterSlot = this.root.querySelector<HTMLElement>("[data-pictionary-roster-slot]")!;
        this.scoreboardSlot = this.root.querySelector<HTMLElement>("[data-pictionary-scoreboard-slot]")!;
        this.wordBankSlot = this.root.querySelector<HTMLElement>("[data-pictionary-word-bank-slot]")!;
        this.resizeObserver =
            typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(() => {
                      this.syncCanvasSize();
                      this.syncCanvasViewportState();
                      this.scheduleCanvasRedraw();
                      this.scheduleRosterLayoutSync();
                  });
        this.resizeObserver?.observe(this.canvasShell);
        this.resizeObserver?.observe(this.rosterSlot);
        this.canvas.addEventListener("pointerdown", this.handleCanvasPointerDown);
        this.canvas.addEventListener("pointermove", this.handleCanvasPointerMove);
        this.canvas.addEventListener("pointerup", this.handleCanvasPointerUp);
        this.canvas.addEventListener("pointerleave", this.handleCanvasPointerUp);
        this.canvas.addEventListener("pointercancel", this.handleCanvasPointerUp);
        this.root.addEventListener("click", this.handleRootClick);
        this.root.addEventListener("input", this.handleRootInput);
        this.root.addEventListener("keydown", this.handleRootKeyDown);
        this.root.addEventListener("pointerdown", this.handleAudioPrime, true);
        this.root.addEventListener("keydown", this.handleAudioPrime, true);
        this.root.addEventListener("focusin", this.handleRootFocusIn);
        this.root.addEventListener("focusout", this.handleRootFocusOut);
        document.addEventListener("visibilitychange", this.handleDocumentVisibilityChange);
        window.visualViewport?.addEventListener("resize", this.handleVisualViewportChange);
        window.visualViewport?.addEventListener("scroll", this.handleVisualViewportChange);
        this.countdownTimer = window.setInterval(() => {
            this.updateCountdowns();
        }, 250);
        this.syncCanvasSize();
    }

    private captureGuessInputSnapshot(): GuessInputSnapshot {
        const activeElement = document.activeElement;
        const input = this.consoleSlot.querySelector<HTMLInputElement>("#pictionary-guess-input");
        const isFocused = Boolean(input && activeElement === input);

        return {
            wasFocused: isFocused,
            selectionStart: isFocused ? input?.selectionStart ?? null : null,
            selectionEnd: isFocused ? input?.selectionEnd ?? null : null
        };
    }

    private restoreGuessInputSnapshot(snapshot: GuessInputSnapshot) {
        if (!snapshot.wasFocused) {
            return;
        }

        const input = this.consoleSlot.querySelector<HTMLInputElement>("#pictionary-guess-input");

        if (!input || input.disabled) {
            return;
        }

        input.focus({
            preventScroll: true
        });

        if (snapshot.selectionStart !== null && snapshot.selectionEnd !== null) {
            input.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd);
        }
    }

    private renderConsoleIntoSlot(room: RoomView, role: RenderRole) {
        const markup = this.renderConsoleMarkup(room, role);
        const renderKey = this.getConsoleRenderKey(room, role);
        const focusedInput = this.consoleSlot.querySelector<HTMLInputElement>("#pictionary-guess-input");

        if (focusedInput && document.activeElement === focusedInput && renderKey === this.consoleRenderKey) {
            return;
        }

        if (markup === this.consoleMarkupCache) {
            this.consoleRenderKey = renderKey;
            return;
        }

        const guessInputSnapshot = this.captureGuessInputSnapshot();
        this.consoleSlot.innerHTML = markup;
        this.consoleMarkupCache = markup;
        this.consoleRenderKey = renderKey;
        this.restoreGuessInputSnapshot(guessInputSnapshot);
    }

    private getConsoleRenderKey(room: RoomView, role: RenderRole): string {
        const pictionary = room.pictionary;
        const viewer = pictionary?.viewer;

        return [
            getLanguage(),
            role,
            pictionary?.roundActive ? "active" : "idle",
            pictionary?.roundComplete ? "complete" : "live",
            pictionary?.currentWordIndex ?? "none",
            viewer?.solved ? "solved" : "unsolved",
            viewer?.livesRemaining ?? "none",
            pictionary?.currentWorth ?? "worth-none",
            pictionary?.nextWorth ?? "next-worth-none",
            pictionary?.nextWorthManual ? "manual-next-worth" : "auto-next-worth",
            this.canViewerGuessNow(pictionary) ? "can" : "cannot",
            viewer?.observeEndsAt ?? "observe-none",
            viewer?.lastFeedbackKind ?? "none",
            viewer?.lastFeedback ?? "",
            viewer?.lastFeedbackSpeedBonus ? "speed-bonus" : "no-speed-bonus",
            viewer?.lastAward
                ? [
                      viewer.lastAward.scoreDelta,
                      viewer.lastAward.casePoints,
                      viewer.lastAward.speedBonusPoints,
                      viewer.lastAward.breakthroughBonusPoints,
                      viewer.lastAward.effortPoints,
                      viewer.lastAward.zeroHeartSolvePoints
                  ].join(",")
                : "no-award"
        ].join(":");
    }

    private getStrokeSignature(strokes: readonly PictionaryStrokeView[]): string {
        return strokes
            .map((stroke) => {
                const lastPoint = stroke.points[stroke.points.length - 1];
                return `${stroke.id}:${stroke.points.length}:${lastPoint?.x.toFixed(4) ?? "x"}:${
                    lastPoint?.y.toFixed(4) ?? "y"
                }`;
            })
            .join("|");
    }

    private renderRoleIntoSlot(room: RoomView, role: RenderRole) {
        const markup = this.renderRoleMarkup(room, role);

        if (markup === this.roleMarkupCache) {
            return;
        }

        const entryGrid = this.roleSlot.querySelector<HTMLElement>(".pictionary-entry-grid");
        const previousScrollTop = entryGrid?.scrollTop ?? 0;

        const reviewScrollTop = this.roleSlot.querySelector<HTMLElement>(".pictionary-word-bank-table-shell")?.scrollTop ?? 0;
        replaceReviewMarkup(this.roleSlot, markup);
        const reviewScroller = this.roleSlot.querySelector<HTMLElement>(".pictionary-word-bank-table-shell");
        if (reviewScroller) reviewScroller.scrollTop = reviewScrollTop;
        this.roleMarkupCache = markup;

        const nextEntryGrid = this.roleSlot.querySelector<HTMLElement>(".pictionary-entry-grid");
        if (nextEntryGrid) {
            nextEntryGrid.scrollTop = previousScrollTop;
        }
    }

    private renderHostUtilityIntoSlot(room: RoomView) {
        const markup = this.renderHostUtilityMarkup(room);

        if (markup === this.hostUtilityMarkupCache) {
            return;
        }

        this.hostUtilitySlot.innerHTML = markup;
        this.hostUtilityMarkupCache = markup;
    }

    private renderRosterIntoSlot(room: RoomView) {
        const markup = this.renderHostRosterMarkup(room);

        if (markup === this.rosterMarkupCache) {
            this.scheduleRosterLayoutSync(room);
            return;
        }

        const rosterBody = this.rosterSlot.querySelector<HTMLElement>(".roster-body");
        const previousScrollTop = rosterBody?.scrollTop ?? 0;

        this.rosterSlot.innerHTML = markup;
        this.rosterMarkupCache = markup;

        const nextRosterBody = this.rosterSlot.querySelector<HTMLElement>(".roster-body");
        if (nextRosterBody) {
            nextRosterBody.scrollTop = previousScrollTop;
            window.requestAnimationFrame(() => {
                nextRosterBody.scrollTop = previousScrollTop;
                this.syncRosterColumnLayout(room);
                this.logRosterLayoutSnapshot(room);
            });
        }
    }

    private scheduleRosterLayoutSync(room = this.currentRoom) {
        if (!room?.pictionary || (this.currentRole !== "host" && this.currentRole !== "host-controller")) {
            return;
        }

        if (this.rosterLayoutFrameId !== null) {
            return;
        }

        this.rosterLayoutFrameId = window.requestAnimationFrame(() => {
            this.rosterLayoutFrameId = null;
            const targetRoom = this.currentRoom?.roomCode === room.roomCode ? this.currentRoom : room;

            if (!targetRoom) {
                return;
            }

            this.syncRosterColumnLayout(targetRoom);
            this.logRosterLayoutSnapshot(targetRoom);
        });
    }

    private syncRosterColumnLayout(room: RoomView) {
        if (!room.pictionary || (this.currentRole !== "host" && this.currentRole !== "host-controller")) {
            return;
        }

        const rosterBody = this.rosterSlot.querySelector<HTMLElement>(".roster-body");
        const rosterList = this.rosterSlot.querySelector<HTMLElement>(".roster-list");

        const result = syncPlayerRosterLayout({
            hidden: this.rosterSlot.hidden,
            rosterBody,
            rosterList
        });

        if (rosterList && result) {
            rosterList.dataset.pictionaryRosterColumns = String(result.columns);
            rosterList.dataset.pictionaryRosterSingleScrollHeight = String(result.singleColumnScrollHeight);
            rosterList.dataset.pictionaryRosterAvailableHeight = String(result.availableHeight);
            rosterList.dataset.pictionaryRosterVisibleHeight = String(result.visibleHeight);
        }
    }

    private logRosterLayoutSnapshot(room: RoomView) {
        if (!isPictionaryDebugEnabled() || !room.pictionary) {
            return;
        }

        const rosterBody = this.rosterSlot.querySelector<HTMLElement>(".roster-body");
        const rosterList = this.rosterSlot.querySelector<HTMLElement>(".roster-list");

        if (!rosterBody || !rosterList) {
            return;
        }

        const participantCount = room.participants.filter(
            (participant) => participant.sessionId !== room.viewer.sessionId && !participant.isController
        ).length;
        const density =
            Array.from(rosterList.classList)
                .find((className) => className.startsWith("player-roster-density-"))
                ?.replace("player-roster-density-", "") ?? "unknown";
        const style = window.getComputedStyle(rosterList);
        const columns = style.gridTemplateColumns.split(" ").filter(Boolean).length;
        const clientHeight = Math.round(rosterBody.clientHeight);
        const scrollHeight = Math.round(rosterBody.scrollHeight);
        const singleColumnScrollHeight = rosterList.dataset.pictionaryRosterSingleScrollHeight ?? "unknown";
        const availableHeight = rosterList.dataset.pictionaryRosterAvailableHeight ?? "unknown";
        const visibleHeight = rosterList.dataset.pictionaryRosterVisibleHeight ?? "unknown";
        const visualViewportHeight = Math.round(window.visualViewport?.height ?? window.innerHeight);
        const visualViewportScale = window.visualViewport?.scale ?? 1;
        const signature = [
            room.roomCode,
            this.currentRole,
            participantCount,
            room.pictionary.teams.length,
            density,
            columns,
            clientHeight,
            scrollHeight,
            singleColumnScrollHeight,
            availableHeight,
            visibleHeight,
            Math.round(window.innerWidth),
            Math.round(window.innerHeight),
            visualViewportHeight,
            visualViewportScale.toFixed(2),
            window.devicePixelRatio.toFixed(2)
        ].join(":");

        if (signature === this.rosterDebugSignature) {
            return;
        }

        this.rosterDebugSignature = signature;
        console.info(
            `[Ziarcadia][PictureDetectives] roster-layout room=${room.roomCode} role=${this.currentRole} participants=${participantCount} teams=${room.pictionary.teams.length} connected=${room.connectedCount} density=${density} columns=${columns} clientHeight=${clientHeight} scrollHeight=${scrollHeight} singleColumnScrollHeight=${singleColumnScrollHeight} availableHeight=${availableHeight} visibleHeight=${visibleHeight} internalScroll=${scrollHeight > clientHeight + 1 ? 1 : 0} viewport=${Math.round(window.innerWidth)}x${Math.round(window.innerHeight)} visualViewportHeight=${visualViewportHeight} visualViewportScale=${visualViewportScale.toFixed(2)} dpr=${window.devicePixelRatio.toFixed(2)} awayGraceMs=${PARTICIPANT_AWAY_GRACE_MS}`
        );
    }

    private renderPlayerBoardIntoSlot(room: RoomView) {
        const markup = this.renderPlayerBoardMarkup(room);
        const wordBankCount = room.pictionary?.wordBank.length ?? 0;
        this.playerBoardSlot.classList.toggle("is-small-bank", wordBankCount > 0 && wordBankCount <= 6);

        if (markup === this.playerBoardMarkupCache) {
            return;
        }

        const scroller = this.playerBoardSlot.querySelector<HTMLElement>(".pictionary-player-word-bank-list");
        const scrollTop = scroller?.scrollTop ?? 0;
        this.playerBoardSlot.innerHTML = markup;
        const nextScroller = this.playerBoardSlot.querySelector<HTMLElement>(".pictionary-player-word-bank-list");
        if (nextScroller) nextScroller.scrollTop = scrollTop;
        this.playerBoardMarkupCache = markup;
        this.syncCaseTranslations();
    }

    private translationUsageSignature = "";
    private translationUsageSentAt = 0;

    private syncCaseTranslations() {
        const room = this.currentRoom;
        const pictionary = room?.pictionary;
        if (!room || !pictionary || this.currentRole !== "player") return;
        this.translations.setCase(
            `ziarcadia:case-translations:${room.roomCode}:${room.viewer.sessionId}`,
            `${pictionary.roundNumber}:${pictionary.currentWordIndex}:${pictionary.roundStartedAt}`,
            pictionary.viewer?.translationRechargeMs ?? getLegacyPictionaryTranslationRechargeMs(pictionary.viewer?.translationRankFraction ?? getPictionaryTranslationRankFraction(
                pictionary.viewer?.score ?? 0, pictionary.teams.map((team) => team.score)
            )),
            pictionary.viewer?.translationRechargeMs === undefined ? "rank-v1" : "guess-efficiency-v1"
        );
        this.translations.setGame(
            `ziarcadia:case-translations:${room.roomCode}:${room.viewer.sessionId}`,
            pictionary.roundNumber, pictionary.roundStartedAt
        );
        const now = this.getServerAlignedNow();
        // Cumulative reports are safe to resend on updates/reconnect; successful sends are throttled.
        if (room.phase === "active" && pictionary.roundStartedAt !== null) {
            const payload = { roundStartedAt: pictionary.roundStartedAt, ...this.translations.getUsage() };
            const signature = JSON.stringify(payload);
            if (signature !== this.translationUsageSignature || now - this.translationUsageSentAt >= 10000) {
                if (this.options.onTranslationUsage?.(payload)) {
                    this.translationUsageSignature = signature;
                    this.translationUsageSentAt = now;
                }
            }
        }
        const state = this.translations.getState(now);
        const bank = this.playerBoardSlot.querySelector<HTMLElement>(".pictionary-player-word-bank");
        const button = bank?.querySelector<HTMLButtonElement>("[data-action=reveal-case-translations]");
        const visible = Boolean(button) && state.phase === "revealing";
        bank?.classList.toggle("is-translations-visible", visible);
        bank?.querySelectorAll<HTMLElement>(".pictionary-case-translation").forEach((translation) => {
            translation.setAttribute("aria-hidden", String(!visible));
        });
        if (button) {
            button.dataset.phase = state.phase;
            button.classList.toggle("needs-discovery", state.phase === "ready" && this.translations.needsDiscovery());
            button.setAttribute("aria-disabled", String(state.phase !== "ready"));
            button.setAttribute("aria-label", t(state.phase === "revealing" ? "caseTranslationsVisible"
                : state.phase === "recharging" ? "caseTranslationsRecharging" : "revealCaseTranslations"));
            const arc = button.querySelector<SVGCircleElement>(".pictionary-translation-ring-progress");
            arc?.setAttribute("stroke-dashoffset", String(100 * (1 - state.progress)));
        }
        const nextAt = button ? state.nextAt : null;
        if (nextAt !== this.translationBoundaryAt) {
            if (this.translationBoundaryTimer !== null) window.clearTimeout(this.translationBoundaryTimer);
            this.translationBoundaryAt = nextAt;
            this.translationBoundaryTimer = nextAt === null ? null : window.setTimeout(() => {
                this.translationBoundaryAt = null;
                this.translationBoundaryTimer = null;
                this.syncCaseTranslations();
            }, Math.max(0, nextAt - now));
        }
    }

    private renderScoreboardIntoSlot(room: RoomView) {
        const markup = this.renderScoreboardMarkup(room);

        if (markup === this.scoreboardMarkupCache) {
            return;
        }

        this.scoreboardSlot.innerHTML = markup;
        this.scoreboardMarkupCache = markup;
    }

    private renderWordBankIntoSlot(room: RoomView) {
        const markup = this.currentRole === "host" || this.currentRole === "host-controller"
            ? this.renderWordBankMarkup(room) : "";

        if (markup === this.wordBankMarkupCache) {
            return;
        }

        const reviewScrollTop = this.wordBankSlot.querySelector<HTMLElement>(".pictionary-word-bank-table-shell")?.scrollTop ?? 0;
        replaceReviewMarkup(this.wordBankSlot, markup);
        const reviewScroller = this.wordBankSlot.querySelector<HTMLElement>(".pictionary-word-bank-table-shell");
        if (reviewScroller) reviewScroller.scrollTop = reviewScrollTop;
        this.wordBankMarkupCache = markup;
    }

    private getServerAlignedNow(): number {
        return Date.now() + this.serverClockOffsetMs;
    }

    private getActiveTryLossAnimation(): TryLossAnimationState | null {
        if (!this.tryLossAnimation) {
            return null;
        }

        if (this.tryLossAnimation.expiresAt <= Date.now()) {
            this.tryLossAnimation = null;
            return null;
        }

        return this.tryLossAnimation;
    }

    private syncTryLossAnimation(room: RoomView, role: RenderRole) {
        const pictionary = room.pictionary;
        const viewer = pictionary?.viewer ?? null;
        const roundKey = pictionary
            ? `${room.roomCode}:${pictionary.roundNumber}:${pictionary.currentWordIndex ?? "none"}`
            : "";

        if (role !== "player" || !pictionary || !viewer || !pictionary.roundActive || pictionary.roundComplete) {
            this.previousViewerRoundKey = roundKey;
            this.previousViewerLivesRemaining = viewer ? clampTryCount(viewer.livesRemaining) : null;
            this.tryLossAnimation = null;
            return;
        }

        const nextLivesRemaining = clampTryCount(viewer.livesRemaining);

        if (roundKey !== this.previousViewerRoundKey) {
            this.previousViewerRoundKey = roundKey;
            this.previousViewerLivesRemaining = nextLivesRemaining;
            this.tryLossAnimation = null;
            return;
        }

        const previousLivesRemaining = this.previousViewerLivesRemaining;

        if (
            previousLivesRemaining !== null &&
            viewer.lastFeedbackKind === "wrong" &&
            nextLivesRemaining < previousLivesRemaining
        ) {
            const fromLives = clampTryCount(previousLivesRemaining);
            const toLives = clampTryCount(nextLivesRemaining);
            const lostCount = fromLives - toLives;

            if (lostCount > 0) {
                this.tryLossAnimation = {
                    fromLives,
                    toLives,
                    lostCount,
                    expiresAt: Date.now() + PICTIONARY_TRY_FALL_ANIMATION_MS + PICTIONARY_TRY_FALL_HOLD_MS
                };
            }
        }

        this.previousViewerRoundKey = roundKey;
        this.previousViewerLivesRemaining = nextLivesRemaining;
    }

    setRoom(room: RoomView) {
        this.currentRoom = room;
        this.serverClockOffsetMs =
            typeof room.serverNow === "number" && Number.isFinite(room.serverNow) ? room.serverNow - Date.now() : 0;
        const nextStrokes = room.pictionary?.strokes ?? [];
        const nextStrokeSignature = this.getStrokeSignature(nextStrokes);

        if (nextStrokeSignature !== this.strokeSignature) {
            const nextLocalStrokes = cloneStrokes(nextStrokes);
            const activeLocalStroke =
                this.pointerIsDown &&
                this.activeStrokeId &&
                room.pictionary?.roundActive &&
                !room.pictionary.roundComplete
                    ? this.strokes.find((stroke) => stroke.id === this.activeStrokeId) ?? null
                    : null;

            // Room-state updates caused by guesses can arrive a fraction behind the
            // host's optimistic drawing. Keep the locally owned in-flight stroke so its
            // tip cannot snap backward (or disappear) while the server catches up.
            if (activeLocalStroke) {
                const activeServerStrokeIndex = nextLocalStrokes.findIndex(
                    (stroke) => stroke.id === activeLocalStroke.id
                );
                const preservedActiveStroke = {
                    id: activeLocalStroke.id,
                    points: activeLocalStroke.points.map((point) => ({ ...point }))
                };

                if (activeServerStrokeIndex >= 0) {
                    nextLocalStrokes[activeServerStrokeIndex] = preservedActiveStroke;
                } else {
                    nextLocalStrokes.push(preservedActiveStroke);
                }
            }

            this.strokes = nextLocalStrokes;
            this.strokeSignature = this.getStrokeSignature(nextLocalStrokes);
        }

        if (!room.pictionary?.roundActive || room.pictionary?.roundComplete) {
            this.cancelLocalStroke();
        }

        const pictionary = room.pictionary;
        this.syncRoundCompleteVisualState(room);
        const nextRole = this.getRenderRole(room);
        const previousViewerScore = this.lastViewerScore;
        const nextViewerScore = pictionary?.viewer?.score ?? null;
        const viewerFeedbackKind = pictionary?.viewer?.lastFeedbackKind ?? null;
        const viewerFeedbackMessage = pictionary?.viewer?.lastFeedback ?? null;
        const viewerAward = pictionary?.viewer?.lastAward ?? null;
        const shouldCelebrateViewerScore =
            nextRole === "player" &&
            previousViewerScore !== null &&
            nextViewerScore !== null &&
            nextViewerScore > previousViewerScore &&
            (viewerFeedbackKind === "correct" ||
                normalizeAwardPoints(viewerAward?.scoreDelta) > 0 ||
                viewerFeedbackMessage === PICTIONARY_EFFORT_FEEDBACK_MESSAGE);
        const viewerScoreDelta =
            shouldCelebrateViewerScore && previousViewerScore !== null && nextViewerScore !== null
                ? nextViewerScore - previousViewerScore
                : 0;

        if (nextRole !== this.currentRole && nextRole === "controller") {
            this.boardOpen = false;
        }

        this.currentRole = nextRole;
        this.lastViewerScore = nextRole === "player" ? nextViewerScore : null;
        this.syncTryLossAnimation(room, nextRole);
        this.syncWorthPreviewBurst(pictionary ?? null, nextRole);
        this.syncTeamScorePopups(pictionary ?? null, nextRole);

        if (
            !pictionary ||
            (nextRole !== "host" && nextRole !== "host-controller") ||
            (this.scoreAdjustSessionId &&
                !room.participants.some((participant) => participant.sessionId === this.scoreAdjustSessionId))
        ) {
            this.scoreAdjustSessionId = null;
        }

        if (pictionary) {
            const liveRoundChanged = pictionary.currentWordIndex !== this.lastLockedWordIndex;

            if (liveRoundChanged) {
                this.selectionDirty = false;
            }

            if (!pictionary.roundActive || pictionary.roundComplete) {
                this.controllerSelectionOpen = true;
            } else if (liveRoundChanged) {
                this.controllerSelectionOpen = false;
            }

            this.lastLockedWordIndex = pictionary.currentWordIndex;

            if (
                this.selectedEntryIndex !== null &&
                !pictionary.entries.some((entry) => entry.index === this.selectedEntryIndex)
            ) {
                this.selectedEntryIndex = null;
            }

            const defaultSelectedPointValue = getDefaultSelectedPointValue(pictionary);

            if (
                !Number.isFinite(this.selectedPointValue) ||
                this.selectedPointValue < PICTIONARY_MIN_POINT_VALUE ||
                this.selectedPointValue > PICTIONARY_MAX_POINT_VALUE
            ) {
                this.selectedPointValue = defaultSelectedPointValue;
                this.pointValueDirty = false;
            }

            if (pictionary.nextWorthManual && !this.pointValueDirty) {
                this.selectedPointValue = defaultSelectedPointValue;
                this.pointValueDirty = true;
            }

            if (!this.pointValueDirty) {
                this.selectedPointValue = defaultSelectedPointValue;
            }

            if (!this.selectionDirty) {
                this.selectedRevealDurationSeconds = clampPictionaryRevealDurationSeconds(
                    pictionary.revealDurationSeconds
                );
                this.selectedHintMode = pictionary.hintMode;

                if (pictionary.currentWordIndex !== null && !this.controllerSelectionOpen) {
                    this.selectedEntryIndex = pictionary.currentWordIndex;
                } else {
                    this.selectedEntryIndex = null;
                }
            }
        } else {
            this.selectedEntryIndex = null;
            this.controllerSelectionOpen = true;
            this.selectionDirty = false;
            this.pointValueDirty = false;
            this.lastLockedWordIndex = null;
            this.selectedHintMode = DEFAULT_PICTIONARY_HINT_MODE;
        }

        if (this.currentRole === "player" && pictionary?.viewer?.lastFeedbackKind === "correct") {
            this.draftGuess = "";
        }

        const showPlayerScoreInHeader = this.currentRole === "player";
        this.pointsLabel.textContent = showPlayerScoreInHeader ? t("points") : t("worth");
        this.pointsValue.textContent = formatPts(
            showPlayerScoreInHeader
                ? pictionary?.viewer?.score ?? 0
                : pictionary
                  ? getPictionaryDisplayWorth(pictionary)
                  : 0
        );
        this.pointsValue
            .closest(".pictionary-head-chip-right")
            ?.classList.toggle("is-player-score", showPlayerScoreInHeader);
        this.roundValue.textContent = String(pictionary?.roundNumber ?? 0);
        if (this.currentRole === "player" && room.viewer.name.trim().length > 0) {
            this.viewerNameChip.id = "";
            this.viewerNameChip.innerHTML = this.renderViewerChipMarkup(room);
            this.viewerNameChip.setAttribute("aria-label", room.viewer.name);
            this.viewerNameChip.removeAttribute("title");
        } else {
            this.viewerNameChip.id = "";
            this.viewerNameChip.innerHTML = "";
            this.viewerNameChip.removeAttribute("aria-label");
            this.viewerNameChip.removeAttribute("title");
        }
        this.viewerNameChip.hidden = this.currentRole !== "player" || room.viewer.name.trim().length === 0;
        this.stageStatus.textContent = localizeStatus(room.statusMessage);
        this.stageStatus.hidden =
            (this.currentRole !== "host" && this.currentRole !== "host-controller") ||
            Boolean(pictionary?.roundActive);
        this.hostTools.hidden =
            !(room.viewer.isHost && Boolean(pictionary?.roundActive) && !Boolean(pictionary?.roundComplete));
        this.renderRoleIntoSlot(room, this.currentRole);
        this.renderHostUtilityIntoSlot(room);
        this.renderRosterIntoSlot(room);
        this.renderConsoleIntoSlot(room, this.currentRole);
        this.renderPlayerBoardIntoSlot(room);
        this.renderScoreboardIntoSlot(room);
        this.renderWordBankIntoSlot(room);
        this.syncShellState();
        this.syncCanvasState();
        this.refreshStageStemMarkup();
        this.syncRoundTimer();
        this.scheduleCanvasRedraw();
        this.updateCountdowns();
        this.syncIdleCanvasAnimation();
        this.syncAudioFeedback(room, pictionary ?? null, this.currentRole, viewerScoreDelta);

        if (viewerScoreDelta > 0) {
            const speedBonusPoints = pictionary?.viewer?.lastFeedbackSpeedBonusPoints ?? 0;
            const breakthroughPoints = getPictionaryBreakthroughFeedbackPoints(viewerFeedbackMessage);
            this.showPlayerScoreCelebration(
                viewerScoreDelta,
                getPictionaryCelebrationDetails(
                    pictionary?.viewer?.lastAward ?? null,
                    breakthroughPoints,
                    speedBonusPoints,
                    viewerFeedbackMessage
                )
            );
        }

        this.syncCapyPeekSchedule(room);
        this.syncCaseMotion(room);
    }

    private syncCaseMotion(room: RoomView) {
        const round = room.pictionary;
        if (!round) { this.caseMotion.cancel(); this.caseMotionKey = ""; return; }
        const review = round.vocabularyReview;
        const reviewing = review?.open && (!round.roundActive || round.roundComplete) && this.currentRole !== "player";
        const phase = reviewing ? `review:${review.index}` : round.roundActive ? round.roundComplete ? "complete" : "drawing" : "waiting";
        const key = `${room.roomCode}:${this.currentRole}:${round.roundStartedAt}:${round.currentWordIndex}:${phase}`;
        if (key === this.caseMotionKey) return;
        const hadSurface = this.caseMotionKey.length > 0;
        const leavingReview = this.caseMotionKey.includes(":review:");
        this.caseMotionKey = key;
        if (!hadSurface) return;
        // Fade paint only: drawing coordinates and guess controls never move.
        let surface = this.canvasShell;
        if (reviewing) surface = this.currentRole === "controller" ? this.roleSlot : this.wordBankSlot;
        else if (phase === "complete" && !leavingReview) {
            surface = this.currentRole === "controller" || this.currentRole === "host-controller" ? this.roleSlot
                : this.roundAnswerValue.hidden ? this.consoleSlot : this.roundAnswerValue;
        }
        this.caseMotion.play(surface, phase, [{ opacity: 0.72 }, { opacity: 1 }], SITE_TUNING.pictionary.motion.surfaceEnterMs);
    }

    applyCanvasSegment(message: PictionaryCanvasSegmentMessage) {
        if (!this.currentRoom || this.currentRoom.roomCode !== message.roomCode) {
            return;
        }

        let stroke = this.strokes.find((candidate) => candidate.id === message.strokeId) ?? null;

        if (!stroke) {
            stroke = {
                id: message.strokeId,
                points: []
            };
            this.strokes.push(stroke);
        }

        this.appendStrokePoints(stroke, message.points);
        this.strokeSignature = this.getStrokeSignature(this.strokes);
        this.scheduleCanvasRedraw();
    }

    destroy() {
        this.reviewAppearance.destroy();
        this.stopObservingReview();
        this.caseMotion.destroy();
        this.clueMotion.destroy();
        if (this.translationBoundaryTimer !== null) window.clearTimeout(this.translationBoundaryTimer);
        this.cancelLocalStroke();
        if (this.idleAnimationFrameId !== null) {
            window.cancelAnimationFrame(this.idleAnimationFrameId);
            this.idleAnimationFrameId = null;
        }
        if (this.canvasRedrawFrameId !== null) {
            window.cancelAnimationFrame(this.canvasRedrawFrameId);
            this.canvasRedrawFrameId = null;
        }
        if (this.rosterLayoutFrameId !== null) {
            window.cancelAnimationFrame(this.rosterLayoutFrameId);
            this.rosterLayoutFrameId = null;
        }
        if (this.celebrationTimerId !== null) {
            window.clearTimeout(this.celebrationTimerId);
            this.celebrationTimerId = null;
        }
        if (this.worthBurstTimerId !== null) {
            window.clearTimeout(this.worthBurstTimerId);
            this.worthBurstTimerId = null;
        }
        if (this.guessInputScrollRestoreTimerId !== null) {
            window.clearTimeout(this.guessInputScrollRestoreTimerId);
            this.guessInputScrollRestoreTimerId = null;
        }
        this.clearCapyPeekTimers();
        this.resizeObserver?.disconnect();
        window.clearInterval(this.countdownTimer);
        this.root.removeEventListener("click", this.handleRootClick);
        this.root.removeEventListener("input", this.handleRootInput);
        this.root.removeEventListener("keydown", this.handleRootKeyDown);
        this.root.removeEventListener("pointerdown", this.handleAudioPrime, true);
        this.root.removeEventListener("keydown", this.handleAudioPrime, true);
        this.root.removeEventListener("focusin", this.handleRootFocusIn);
        this.root.removeEventListener("focusout", this.handleRootFocusOut);
        document.removeEventListener("visibilitychange", this.handleDocumentVisibilityChange);
        this.canvas.removeEventListener("pointerdown", this.handleCanvasPointerDown);
        this.canvas.removeEventListener("pointermove", this.handleCanvasPointerMove);
        this.canvas.removeEventListener("pointerup", this.handleCanvasPointerUp);
        this.canvas.removeEventListener("pointerleave", this.handleCanvasPointerUp);
        this.canvas.removeEventListener("pointercancel", this.handleCanvasPointerUp);
        window.visualViewport?.removeEventListener("resize", this.handleVisualViewportChange);
        window.visualViewport?.removeEventListener("scroll", this.handleVisualViewportChange);
        this.audio.dispose();
        this.root.classList.remove("pictionary-view-root");
    }

    private readonly handleAudioPrime = () => {
        void this.audio.prepare();
    };

    private readonly handleRootClick = (event: Event) => {
        const target = event.target as HTMLElement | null;

        if (!target || !this.currentRoom?.pictionary) {
            return;
        }

        const capyPeekButton = target.closest<HTMLButtonElement>("[data-pictionary-capy-peek]");

        if (capyPeekButton && !capyPeekButton.hidden) {
            event.preventDefault();
            this.reactToCapyPeekTap();
            return;
        }

        const suggestionButton = target.closest<HTMLButtonElement>("[data-pictionary-guess-suggestion]");
        const focusGuessInputControl = target.closest("[data-action=\"focus-guess-input\"]");
        const guessButton = target.closest("[data-action=\"guess-submit\"]");
        const nextRoundButton = target.closest("[data-action=\"next-round\"]");
        const openPickerButton = target.closest("[data-action=\"open-controller-picker\"]");
        const closePickerButton = target.closest("[data-action=\"close-controller-picker\"]");
        const toggleBoardButton = target.closest("[data-action=\"toggle-team-board\"]");
        const closeBoardButton = target.closest("[data-action=\"close-team-board\"]");
        const toggleWordBankButton = target.closest("[data-action=\"toggle-word-bank\"]");
        const closeWordBankButton = target.closest("[data-action=\"close-word-bank\"]");
        const decrementPointsButton = target.closest("[data-action=\"decrement-points\"]");
        const incrementPointsButton = target.closest("[data-action=\"increment-points\"]");
        const decrementRevealButton = target.closest("[data-action=\"decrement-reveal-duration\"]");
        const incrementRevealButton = target.closest("[data-action=\"increment-reveal-duration\"]");
        const worthModeButton = target.closest<HTMLElement>("[data-action=\"set-worth-mode\"]");
        const hintModeButton = target.closest<HTMLElement>("[data-action=\"set-hint-mode\"]");
        const undoButton = target.closest("[data-action=\"undo-stroke\"]");
        const clearButton = target.closest("[data-action=\"clear-canvas\"]");
        const switchControllerRoomButton = target.closest("[data-action=\"switch-controller-room\"]");
        const playerHistoryMenu = target.closest<HTMLElement>("[data-player-history-menu]");
        const playerHistoryMenuToggle = target.closest<HTMLElement>("[data-player-history-menu-toggle]");
        const playerHistoryDownloadButton = target.closest<HTMLElement>("[data-player-history-download]");
        const rosterActionsToggle = target.closest<HTMLElement>("[data-action=\"toggle-roster-actions\"]");
        const renameParticipantButton = target.closest<HTMLElement>("[data-action=\"rename-roster-participant\"]");
        const promptRenameParticipantButton = target.closest<HTMLElement>("[data-action=\"prompt-rename-roster-participant\"]");
        const kickParticipantButton = target.closest<HTMLElement>("[data-action=\"kick-roster-participant\"]");
        const scoreAdjustToggle = target.closest<HTMLElement>("[data-action=\"toggle-score-adjust\"]");
        const scoreAdjustButton = target.closest<HTMLElement>("[data-action=\"adjust-score\"]");
        const entryButton = target.closest<HTMLElement>("[data-entry-index]");
        const clickedRosterTransientControl = Boolean(
            playerHistoryMenu ||
                playerHistoryDownloadButton ||
                rosterActionsToggle ||
                renameParticipantButton ||
                promptRenameParticipantButton ||
                kickParticipantButton ||
                scoreAdjustToggle ||
                scoreAdjustButton
        );

        if (this.playerHistoryMenuOpen && !playerHistoryMenu) {
            this.playerHistoryMenuOpen = false;
            this.rosterMarkupCache = "";
            this.renderRosterIntoSlot(this.currentRoom);
        }

        if (!clickedRosterTransientControl && (this.openRosterActionsKey || this.scoreAdjustSessionId)) {
            this.openRosterActionsKey = null;
            this.scoreAdjustSessionId = null;
            this.rosterMarkupCache = "";
            this.renderRosterIntoSlot(this.currentRoom);
        }

        if (suggestionButton) {
            const suggestion = suggestionButton.getAttribute("data-pictionary-guess-suggestion") ?? "";

            if (suggestion) {
                this.draftGuess = suggestion;
                const input = this.consoleSlot.querySelector<HTMLInputElement>("#pictionary-guess-input");

                if (input) {
                    input.value = suggestion;
                }

                this.options.onGuess(suggestion);
            }
            return;
        }

        if (focusGuessInputControl) {
            const input = this.consoleSlot.querySelector<HTMLInputElement>("#pictionary-guess-input");

            if (input && !input.disabled) {
                input.focus({
                    preventScroll: true
                });
            }
            return;
        }

        if (guessButton) {
            this.options.onGuess(this.draftGuess);
            return;
        }

        if (nextRoundButton) {
            if (
                this.currentRoom.pictionary.roundActive &&
                !this.currentRoom.pictionary.roundComplete
            ) {
                this.options.onEndRound();
                return;
            }

            if (this.selectedEntryIndex === null) {
                return;
            }

            this.controllerSelectionOpen = false;
            this.options.onNextRound(
                this.selectedEntryIndex,
                this.selectedPointValue,
                this.selectedRevealDurationSeconds,
                this.selectedHintMode
            );
            this.rerenderRoleSlot();
            this.updateCountdowns();
            return;
        }

        if (openPickerButton) {
            this.controllerSelectionOpen = true;
            this.rerenderRoleSlot();
            this.updateCountdowns();
            return;
        }

        if (closePickerButton) {
            if (this.currentRoom?.pictionary?.roundActive && !this.currentRoom.pictionary.roundComplete) {
                this.selectionDirty = false;
                this.selectedEntryIndex = this.currentRoom.pictionary.currentWordIndex;
                this.selectedPointValue = this.currentRoom.pictionary.pointValue;
                this.selectedRevealDurationSeconds = clampPictionaryRevealDurationSeconds(
                    this.currentRoom.pictionary.revealDurationSeconds
                );
                this.selectedHintMode = this.currentRoom.pictionary.hintMode;
            }
            this.controllerSelectionOpen = false;
            this.rerenderRoleSlot();
            this.updateCountdowns();
            return;
        }

        if (toggleBoardButton) {
            this.boardOpen = !this.boardOpen;
            this.wordBankOpen = false;
            this.renderConsoleIntoSlot(this.currentRoom, this.currentRole);
            this.syncShellState();
            return;
        }

        if (closeBoardButton) {
            this.boardOpen = false;
            this.syncShellState();
            return;
        }

        if (toggleWordBankButton) {
            this.wordBankOpen = !this.wordBankOpen;
            this.renderPlayerBoardIntoSlot(this.currentRoom);
            this.syncShellState();
            this.playerBoardSlot.querySelector<HTMLElement>("[data-action=toggle-word-bank]")?.focus();
            return;
        }

        if (target.closest("[data-action=reveal-case-translations]") && this.currentRole === "player") {
            this.syncCaseTranslations();
            if (!this.currentRoom.pictionary?.noTranslations) {
                this.translations.reveal(this.getServerAlignedNow());
                this.syncCaseTranslations();
            }
            return;
        }

        if (closeWordBankButton) {
            this.options.onVocabularyReview(false, this.currentRoom.pictionary?.vocabularyReview?.index ?? 0);
            return;
        }

        const reviewButton = target.closest<HTMLElement>("[data-review-index], [data-action=show-vocabulary-review]");
        if (reviewButton) {
            this.options.onVocabularyReview(true, Number(reviewButton.dataset.reviewIndex ?? 0));
            return;
        }

        if (decrementPointsButton || incrementPointsButton) {
            const delta = decrementPointsButton ? -1 : 1;
            this.selectedPointValue = Math.min(
                PICTIONARY_MAX_POINT_VALUE,
                Math.max(PICTIONARY_MIN_POINT_VALUE, this.selectedPointValue + delta)
            );
            this.selectionDirty = true;
            this.pointValueDirty = true;
            this.options.onPreviewWorth(this.selectedPointValue, true);
            this.rerenderRoleSlot(true);
            this.updateCountdowns();
            return;
        }

        if (decrementRevealButton || incrementRevealButton) {
            const delta = decrementRevealButton ? -PICTIONARY_REVEAL_DURATION_STEP_SECONDS : PICTIONARY_REVEAL_DURATION_STEP_SECONDS;
            this.selectedRevealDurationSeconds = clampPictionaryRevealDurationSeconds(
                this.selectedRevealDurationSeconds + delta
            );
            this.selectionDirty = true;
            this.rerenderRoleSlot(true);
            this.updateCountdowns();
            return;
        }

        if (worthModeButton) {
            const worthMode = worthModeButton.getAttribute("data-worth-mode");

            if (worthMode === "auto") {
                this.pointValueDirty = false;
                this.selectedPointValue = getDefaultSelectedPointValue(this.currentRoom.pictionary);
                this.options.onPreviewWorth(this.selectedPointValue, false);
            } else if (worthMode === "manual") {
                this.pointValueDirty = true;
                this.options.onPreviewWorth(this.selectedPointValue, true);
            }

            this.selectionDirty = true;
            this.rerenderRoleSlot(true);
            this.updateCountdowns();
            return;
        }

        if (hintModeButton) {
            const hintMode = hintModeButton.getAttribute("data-hint-mode");

            if (hintMode === "over-time" || hintMode === "all-at-once" || hintMode === "off") {
                this.selectedHintMode = hintMode;
                if (hintMode === "all-at-once") {
                    this.selectedRevealDurationSeconds = 0;
                }
                this.selectionDirty = true;
                this.rerenderRoleSlot(true);
                this.updateCountdowns();
            }
            return;
        }

        if (undoButton) {
            this.options.onUndo();
            return;
        }

        if (clearButton) {
            this.options.onClear();
            return;
        }

        if (switchControllerRoomButton) {
            event.preventDefault();
            this.submitControllerRoomChange();
            return;
        }

        if (playerHistoryMenuToggle) {
            event.preventDefault();
            event.stopPropagation();
            this.playerHistoryMenuOpen = !this.playerHistoryMenuOpen;
            this.openRosterActionsKey = null;
            this.scoreAdjustSessionId = null;
            this.rosterMarkupCache = "";
            this.renderRosterIntoSlot(this.currentRoom);
            return;
        }

        if (playerHistoryDownloadButton) {
            event.preventDefault();
            event.stopPropagation();
            this.playerHistoryMenuOpen = false;
            downloadPlayerHistory(this.currentRoom);
            this.rosterMarkupCache = "";
            this.renderRosterIntoSlot(this.currentRoom);
            return;
        }

        if (rosterActionsToggle) {
            event.preventDefault();
            event.stopPropagation();
            const actionsKey = rosterActionsToggle.getAttribute("data-roster-action-key") ?? "";
            this.openRosterActionsKey = actionsKey && this.openRosterActionsKey !== actionsKey ? actionsKey : null;
            this.scoreAdjustSessionId = null;
            this.playerHistoryMenuOpen = false;
            if (this.currentRoom) {
                this.rosterMarkupCache = "";
                this.renderRosterIntoSlot(this.currentRoom);
            }
            return;
        }

        if (renameParticipantButton) {
            const sessionId = renameParticipantButton.getAttribute("data-session-id") ?? "";
            this.openRosterActionsKey = null;
            this.scoreAdjustSessionId = null;
            if (this.currentRoom) {
                this.rosterMarkupCache = "";
                this.renderRosterIntoSlot(this.currentRoom);
            }

            if (sessionId) {
                this.options.onRenameParticipant(
                    sessionId,
                    `#${buildPictionaryRosterActionsTriggerId(sessionId)}`
                );
            }
            return;
        }

        if (promptRenameParticipantButton) {
            const sessionId = promptRenameParticipantButton.getAttribute("data-session-id") ?? "";
            this.openRosterActionsKey = null;
            this.scoreAdjustSessionId = null;
            if (this.currentRoom) {
                this.rosterMarkupCache = "";
                this.renderRosterIntoSlot(this.currentRoom);
            }

            if (sessionId) {
                this.options.onPromptRenameParticipant(sessionId);
            }
            return;
        }

        if (kickParticipantButton) {
            const sessionId = kickParticipantButton.getAttribute("data-session-id") ?? "";
            this.openRosterActionsKey = null;
            this.scoreAdjustSessionId = null;
            if (this.currentRoom) {
                this.rosterMarkupCache = "";
                this.renderRosterIntoSlot(this.currentRoom);
            }

            if (sessionId) {
                this.options.onKickParticipant(sessionId);
            }
            return;
        }

        if (scoreAdjustToggle) {
            event.preventDefault();
            event.stopPropagation();
            const sessionId = scoreAdjustToggle.getAttribute("data-session-id") ?? "";
            this.scoreAdjustSessionId =
                sessionId && this.scoreAdjustSessionId !== sessionId ? sessionId : null;
            this.openRosterActionsKey = null;
            if (this.currentRoom) {
                this.rosterMarkupCache = "";
                this.renderRosterIntoSlot(this.currentRoom);
            }
            return;
        }

        if (scoreAdjustButton) {
            event.preventDefault();
            event.stopPropagation();
            const sessionId = scoreAdjustButton.getAttribute("data-session-id") ?? "";
            const rawDelta = Number.parseInt(scoreAdjustButton.getAttribute("data-score-delta") ?? "", 10);
            const delta = rawDelta > 0 ? 1 : rawDelta < 0 ? -1 : 0;

            if (sessionId && delta !== 0) {
                this.scoreAdjustSessionId = null;
                this.rosterMarkupCache = "";
                if (this.currentRoom) {
                    this.renderRosterIntoSlot(this.currentRoom);
                }
                this.options.onAdjustScore(sessionId, delta);
            }
            return;
        }

        if (entryButton) {
            const nextIndex = Number.parseInt(entryButton.getAttribute("data-entry-index") ?? "", 10);

            if (Number.isFinite(nextIndex)) {
                this.selectedEntryIndex = nextIndex;
                this.selectionDirty = true;
                this.rerenderRoleSlot(true);
                this.updateCountdowns();
            }
            return;
        }
    };

    private readonly handleRootInput = (event: Event) => {
        const target = event.target as HTMLInputElement | null;

        if (!target) {
            return;
        }

        if (target.id === "pictionary-controller-room-code-input") {
            const normalized = formatControllerRoomCode(target.value);
            target.value = normalized;
            return;
        }

        if (target.id === "pictionary-guess-input") {
            this.draftGuess = target.value;
            this.syncGuessSuggestions();
        }
    };

    private readonly handleRootKeyDown = (event: KeyboardEvent) => {
        const target = event.target as HTMLInputElement | null;
        const round = this.currentRoom?.pictionary;
        if (this.currentRole !== "player" && round?.vocabularyReview?.open && (!round.roundActive || round.roundComplete)) {
            const next = reviewIndexForKey(event, round.vocabularyReview.index, round.wordBank.length);
            if (next !== null) { event.preventDefault(); this.options.onVocabularyReview(true, next); return; }
        }

        if (event.key === "Escape" && this.wordBankOpen && this.currentRoom) {
            event.preventDefault();
            this.wordBankOpen = false;
            this.renderPlayerBoardIntoSlot(this.currentRoom);
            this.syncShellState();
            this.playerBoardSlot.querySelector<HTMLElement>("[data-action=toggle-word-bank]")?.focus();
            return;
        }

        if (!target || event.key !== "Enter") {
            return;
        }

        if (target.id === "pictionary-guess-input") {
            event.preventDefault();
            this.options.onGuess(this.draftGuess);
            return;
        }

        if (target.id === "pictionary-controller-room-code-input") {
            event.preventDefault();
            this.submitControllerRoomChange(target);
        }
    };

    private submitControllerRoomChange(input = this.root.querySelector<HTMLInputElement>("#pictionary-controller-room-code-input")) {
        if (!input) {
            return;
        }

        const roomCode = formatControllerRoomCode(input.value);
        input.value = roomCode;

        if (!isValidRoomCode(roomCode) || roomCode === this.currentRoom?.roomCode) {
            return;
        }

        this.options.onControllerRoomChange(roomCode);
    }

    private readonly handleRootFocusIn = (event: FocusEvent) => {
        const target = event.target as HTMLElement | null;

        if (!target || target.id !== "pictionary-guess-input") {
            return;
        }

        this.syncGuessInputDock();
        this.syncCapyPeekVisibilityGuard();
    };

    private readonly handleRootFocusOut = () => {
        window.setTimeout(() => {
            this.syncGuessInputDock();
        }, 0);
    };

    private readonly handleVisualViewportChange = () => {
        this.syncGuessInputDock();
        this.scheduleRosterLayoutSync();
    };

    private readonly handleCanvasPointerDown = (event: PointerEvent) => {
        if (!this.canDraw() || this.activeDrawingPointerId !== null) {
            return;
        }

        const [point] = this.getNormalizedCanvasPoints(event, {
            includeCoalesced: false
        });

        if (!point) {
            return;
        }

        this.pointerIsDown = true;
        this.activeDrawingPointerId = event.pointerId;
        this.canvas.setPointerCapture(event.pointerId);
        this.beginLocalStroke(point);
    };

    private readonly handleCanvasPointerMove = (event: PointerEvent) => {
        if (
            event.pointerId !== this.activeDrawingPointerId ||
            !this.pointerIsDown ||
            !this.activeStrokeId ||
            !this.canDraw()
        ) {
            return;
        }

        const points = this.getNormalizedCanvasPoints(event);

        if (points.length === 0) {
            return;
        }

        this.appendLocalStrokePoints(points);
    };

    private readonly handleCanvasPointerUp = (event: PointerEvent) => {
        if (event.pointerId !== this.activeDrawingPointerId) {
            return;
        }

        const finishingStrokeId = this.activeStrokeId;

        if (this.pointerIsDown) {
            if (event.type === "pointerup" && this.canDraw()) {
                this.appendLocalStrokePoints(
                    this.getNormalizedCanvasPoints(event).filter((point) => !point.gapBefore)
                );
            }

            this.flushQueuedStrokePoints();

            if (finishingStrokeId) {
                this.options.onDraw({
                    strokeId: finishingStrokeId,
                    begin: false,
                    end: true,
                    points: []
                });
            }
        }

        this.pointerIsDown = false;
        this.activeDrawingPointerId = null;
        this.activeStrokeId = null;

        if (this.canvas.hasPointerCapture(event.pointerId)) {
            this.canvas.releasePointerCapture(event.pointerId);
        }
    };

    private getRenderRole(room: RoomView): RenderRole {
        if (room.viewer.isHost) {
            return room.viewer.isController ? "host-controller" : "host";
        }

        return room.viewer.isController ? "controller" : "player";
    }

    private syncShellState() {
        this.reviewAppearance.refresh();
        const isHostRole = this.currentRole === "host" || this.currentRole === "host-controller";
        this.shell.classList.toggle(
            "is-host-view",
            isHostRole
        );
        this.shell.classList.toggle("is-host-controller-view", this.currentRole === "host-controller");
        this.shell.classList.toggle("is-player-view", this.currentRole === "player");
        this.shell.classList.toggle("is-controller-view", this.currentRole === "controller");
        this.shell.classList.toggle(
            "is-team-board-open",
            this.currentRole !== "controller" && this.boardOpen
        );
        this.sideCard.hidden = this.currentRole === "player";
        this.playerBoardSlot.hidden = this.currentRole !== "player" || this.playerBoardSlot.innerHTML.trim().length === 0;
        this.roleSlot.hidden = this.currentRole === "player" || this.currentRole === "host";
        this.hostUtilitySlot.hidden = !isHostRole;
        this.rosterSlot.hidden = !isHostRole;
        this.scoreboardSlot.hidden = this.currentRole !== "host" && this.currentRole !== "host-controller" || !this.boardOpen;
        const review = this.currentRoom?.pictionary?.vocabularyReview;
        const reviewOpen = (isHostRole || this.currentRole === "controller") && review?.open === true &&
            (!this.currentRoom?.pictionary?.roundActive || this.currentRoom.pictionary.roundComplete);
        this.shell.classList.toggle("is-vocabulary-review", isHostRole && reviewOpen);
        this.shell.classList.toggle("is-case-file-expanded", this.currentRole === "player" && this.wordBankOpen);
        this.wordBankSlot.hidden = !isHostRole || !reviewOpen;
        const reviewPosition = reviewOpen ? String(review?.index ?? 0) : "";
        if (reviewPosition !== this.lastReviewPosition) {
            this.lastReviewPosition = reviewPosition;
            if (reviewOpen) {
                const reviewRoot = isHostRole ? this.wordBankSlot : this.roleSlot;
                centerReviewWord(reviewRoot, review?.index ?? 0);
            }
        }
        this.syncCanvasViewportState();
        this.syncGuessInputDock();
        this.scheduleRosterLayoutSync();
    }

    private canDraw(): boolean {
        return Boolean(
            this.currentRoom?.viewer.isHost &&
                this.currentRoom?.mode &&
                this.currentRoom.pictionary?.roundActive &&
                !this.currentRoom.pictionary?.roundComplete
        );
    }

    private cancelLocalStroke() {
        this.pointerIsDown = false;
        this.activeDrawingPointerId = null;
        this.activeStrokeId = null;
        this.queuedStrokePoints = [];

        if (this.flushFrameId !== null) {
            window.cancelAnimationFrame(this.flushFrameId);
            this.flushFrameId = null;
        }
    }

    private beginLocalStroke(point: PictionaryCanvasPoint) {
        this.activeStrokeId = createStrokeId();
        this.queuedStrokePoints = [];
        const cleanPoint = { x: point.x, y: point.y };

        this.strokes.push({
            id: this.activeStrokeId,
            points: [cleanPoint]
        });
        this.strokeSignature = this.getStrokeSignature(this.strokes);
        this.scheduleCanvasRedraw();
        this.options.onDraw({
            strokeId: this.activeStrokeId,
            begin: true,
            end: false,
            points: [cleanPoint]
        });
    }

    private appendLocalStrokePoints(points: readonly NormalizedCanvasPoint[]) {
        if (!this.activeStrokeId || points.length === 0) {
            return;
        }

        let stroke = this.strokes.find((candidate) => candidate.id === this.activeStrokeId);

        if (!stroke) {
            return;
        }

        for (const point of points) {
            const cleanPoint = { x: point.x, y: point.y };

            if (point.gapBefore) {
                stroke = this.restartLocalStrokeAfterGap(cleanPoint);
                continue;
            }

            stroke.points.push(cleanPoint);
            this.queuedStrokePoints.push(cleanPoint);
        }

        this.strokeSignature = this.getStrokeSignature(this.strokes);
        this.scheduleCanvasRedraw();

        if (this.flushFrameId === null) {
            this.flushFrameId = window.requestAnimationFrame(() => {
                this.flushFrameId = null;
                this.flushQueuedStrokePoints();
            });
        }
    }

    private restartLocalStrokeAfterGap(point: PictionaryCanvasPoint): PictionaryStrokeView {
        const finishingStrokeId = this.activeStrokeId;

        this.flushQueuedStrokePoints();

        if (finishingStrokeId) {
            this.options.onDraw({
                strokeId: finishingStrokeId,
                begin: false,
                end: true,
                points: []
            });
        }

        this.activeStrokeId = createStrokeId();
        this.queuedStrokePoints = [];

        const cleanPoint = { x: point.x, y: point.y };
        const stroke = {
            id: this.activeStrokeId,
            points: [cleanPoint]
        };

        this.strokes.push(stroke);
        this.options.onDraw({
            strokeId: this.activeStrokeId,
            begin: true,
            end: false,
            points: [cleanPoint]
        });

        return stroke;
    }

    private appendStrokePoints(stroke: PictionaryStrokeView, points: readonly PictionaryCanvasPoint[]) {
        if (points.length === 0) {
            return;
        }

        if (containsPointRun(stroke.points, points)) {
            return;
        }

        const nextPoints = points.map((point) => ({ ...point }));
        const overlapCount = getPointTailOverlapCount(stroke.points, nextPoints);

        if (overlapCount > 0) {
            nextPoints.splice(0, overlapCount);
        }

        if (nextPoints.length === 0) {
            return;
        }

        stroke.points.push(...nextPoints);
    }

    private flushQueuedStrokePoints() {
        if (!this.activeStrokeId || this.queuedStrokePoints.length === 0) {
            this.queuedStrokePoints = [];
            return;
        }

        const points = this.queuedStrokePoints.map((point) => ({ ...point }));
        this.queuedStrokePoints = [];

        for (let index = 0; index < points.length; index += PICTIONARY_MAX_SEGMENT_POINTS) {
            this.options.onDraw({
                strokeId: this.activeStrokeId,
                begin: false,
                end: false,
                points: points.slice(index, index + PICTIONARY_MAX_SEGMENT_POINTS)
            });
        }
    }

    private shouldCropCanvasBottom(): boolean {
        const pictionary = this.currentRoom?.pictionary;

        return Boolean(
            this.currentRole === "player" &&
                pictionary?.roundActive
        );
    }

    private getCanvasDisplayBottomCrop(width: number, height: number): number {
        if (!this.shouldCropCanvasBottom() || width <= 0 || height <= 0) {
            return 0;
        }

        return Math.min(
            DRAW_SAFE_BOTTOM_INSET_MAX_PX,
            Math.max(DRAW_SAFE_BOTTOM_INSET_MIN_PX, height * DRAW_SAFE_BOTTOM_INSET_RATIO)
        );
    }

    private getCanvasVisibleDrawingHeight(width: number, height: number): number {
        return Math.max(1, height - this.getCanvasDisplayBottomCrop(width, height));
    }

    private getNormalizedCanvasPoints(
        event: PointerEvent,
        options: { includeCoalesced?: boolean } = {}
    ): NormalizedCanvasPoint[] {
        const rect = this.canvas.getBoundingClientRect();

        if (rect.width <= 0 || rect.height <= 0) {
            return [];
        }

        const drawableHeight = rect.height;
        const includeCoalesced = options.includeCoalesced !== false;
        const coalescedEvents =
            includeCoalesced && typeof event.getCoalescedEvents === "function" ? event.getCoalescedEvents() : [];
        const rawEvents = coalescedEvents.length > 0 ? coalescedEvents : [event];
        const points: NormalizedCanvasPoint[] = [];
        const activeStroke = this.activeStrokeId
            ? this.strokes.find((candidate) => candidate.id === this.activeStrokeId)
            : null;
        let previousPoint = activeStroke?.points[activeStroke.points.length - 1] ?? null;
        let needsGapBeforeNextPoint = false;
        const shortDrawableSide = Math.min(rect.width, drawableHeight);
        const jumpThresholdPx = Math.max(72, Math.min(MAX_STROKE_JUMP_PX, shortDrawableSide * 0.22));

        for (const rawEvent of rawEvents) {
            const localX = rawEvent.clientX - rect.left;
            const localY = rawEvent.clientY - rect.top;

            if (localX < 0 || localX > rect.width || localY < 0 || localY > drawableHeight) {
                needsGapBeforeNextPoint = true;
                previousPoint = null;
                continue;
            }

            const point = {
                x: Math.min(1, Math.max(0, localX / rect.width)),
                y: Math.min(1, Math.max(0, localY / drawableHeight))
            };

            if (needsGapBeforeNextPoint && activeStroke?.points.length) {
                points.push({
                    ...point,
                    gapBefore: true
                });
                previousPoint = point;
                needsGapBeforeNextPoint = false;
                continue;
            }

            needsGapBeforeNextPoint = false;

            if (previousPoint) {
                const distancePx = Math.hypot(
                    (point.x - previousPoint.x) * rect.width,
                    (point.y - previousPoint.y) * drawableHeight
                );

                if (distancePx <= MIN_POINT_DISTANCE_PX) {
                    continue;
                }

                if (distancePx > jumpThresholdPx) {
                    points.push({
                        ...point,
                        gapBefore: true
                    });
                    previousPoint = point;
                    continue;
                }

                const segmentCount = Math.max(1, Math.ceil(distancePx / MAX_POINT_GAP_PX));

                for (let segmentIndex = 1; segmentIndex <= segmentCount; segmentIndex += 1) {
                    const interpolatedPoint = {
                        x: previousPoint.x + ((point.x - previousPoint.x) * segmentIndex) / segmentCount,
                        y: previousPoint.y + ((point.y - previousPoint.y) * segmentIndex) / segmentCount
                    };
                    const lastPoint = points[points.length - 1] ?? previousPoint;

                    if (
                        Math.abs(lastPoint.x - interpolatedPoint.x) < 0.0005 &&
                        Math.abs(lastPoint.y - interpolatedPoint.y) < 0.0005
                    ) {
                        continue;
                    }

                    points.push(interpolatedPoint);
                }
            } else {
                points.push(point);
            }

            previousPoint = point;
        }

        return points;
    }

    private syncCanvasSize() {
        const rect = this.canvasShell.getBoundingClientRect();
        const devicePixelRatio = window.devicePixelRatio || 1;
        const width = Math.max(1, Math.round(rect.width * devicePixelRatio));
        const height = Math.max(1, Math.round(rect.height * devicePixelRatio));

        if (this.canvas.width === width && this.canvas.height === height) {
            return;
        }

        this.canvas.width = width;
        this.canvas.height = height;
    }

    private syncCanvasViewportState() {
        const rect = this.canvasShell.getBoundingClientRect();
        const bottomCrop = this.getCanvasDisplayBottomCrop(rect.width, rect.height);

        this.shell.classList.toggle("has-drawing-bottom-crop", bottomCrop > 0);
        this.shell.style.setProperty("--pictionary-drawing-bottom-crop", `${bottomCrop}px`);
    }

    private syncCanvasState() {
        if (!this.currentRoom?.pictionary) {
            return;
        }

        this.canvas.classList.toggle("is-drawable", this.canDraw());
    }

    private syncRoundCompleteVisualState(room: RoomView) {
        const pictionary = room.pictionary;

        if (!pictionary?.roundActive || !pictionary.roundComplete || !pictionary.roundCompletedAt) {
            this.roundCompleteVisualKey = "";
            this.roundCompleteVisualStartedAt = null;
            return;
        }

        const roundCompleteVisualKey = [
            room.roomCode,
            pictionary.roundNumber,
            pictionary.currentWordIndex ?? "none",
            pictionary.roundCompletedAt
        ].join(":");

        if (roundCompleteVisualKey !== this.roundCompleteVisualKey) {
            this.roundCompleteVisualKey = roundCompleteVisualKey;
            this.roundCompleteVisualStartedAt = Date.now();
        }
    }

    private syncAudioFeedback(
        room: RoomView,
        pictionary: RoomView["pictionary"],
        role: RenderRole,
        viewerScoreDelta: number
    ) {
        this.audio.syncThinkingMusic(
            room.viewer.isHost,
            Boolean(pictionary?.roundActive && !pictionary.roundComplete),
            pictionary?.roundNumber ?? 1
        );

        if (!pictionary) {
            this.audioStateInitialized = false;
            this.lastWordStartSoundKey = "";
            this.lastWordFinishedSoundKey = "";
            this.teamScoreBySessionId.clear();
            return;
        }

        if (pictionary.roundActive || pictionary.entries.length > 0) {
            void this.audio.prepare();
        }

        const roundKey = [
            room.roomCode,
            pictionary.roundNumber,
            pictionary.currentWordIndex ?? "none",
            pictionary.roundStartedAt ?? "none"
        ].join(":");
        const wordStartSoundKey = pictionary.roundActive && pictionary.roundStartedAt ? roundKey : "";
        const wordFinishedSoundKey =
            pictionary.roundComplete && pictionary.roundCompletedAt
                ? `${roundKey}:${pictionary.roundCompletedAt}`
                : "";
        const shouldPlayDisplayScoreAward =
            (role === "host" || role === "host-controller") &&
            pictionary.teams.some((team) => {
                const previousScore = this.teamScoreBySessionId.get(team.sessionId);
                return previousScore !== undefined && team.score > previousScore;
            });

        if (this.audioStateInitialized) {
            if (wordStartSoundKey && wordStartSoundKey !== this.lastWordStartSoundKey) {
                this.audio.playWordStart();
            }

            if (wordFinishedSoundKey && wordFinishedSoundKey !== this.lastWordFinishedSoundKey) {
                this.audio.playWordFinished();
            }

            if (viewerScoreDelta > 0 || shouldPlayDisplayScoreAward) {
                this.audio.playRightAnswer();
            }
        }

        this.lastWordStartSoundKey = wordStartSoundKey;
        this.lastWordFinishedSoundKey = wordFinishedSoundKey;
        this.teamScoreBySessionId = new Map(pictionary.teams.map((team) => [team.sessionId, team.score]));
        this.audioStateInitialized = true;
    }

    private syncTeamScorePopups(pictionary: RoomView["pictionary"], role: RenderRole) {
        const now = Date.now();

        for (const [sessionId, popup] of this.teamScorePopupBySessionId.entries()) {
            if (popup.expiresAt <= now) {
                this.teamScorePopupBySessionId.delete(sessionId);
            }
        }

        if (!pictionary) {
            this.teamScorePopupBySessionId.clear();
            return;
        }

        if (role !== "host" && role !== "host-controller" && role !== "player") {
            this.teamScorePopupBySessionId.clear();
            return;
        }

        for (const team of pictionary.teams) {
            const previousScore = this.teamScoreBySessionId.get(team.sessionId);

            if (previousScore === undefined || team.score <= previousScore) {
                continue;
            }

            this.teamScorePopupBySessionId.set(team.sessionId, {
                points: team.score - previousScore,
                sequence: (this.teamScorePopupSequence += 1),
                expiresAt: now + PICTIONARY_TEAM_SCORE_POPUP_MS
            });
        }
    }

    private renderTeamScorePopupMarkup(sessionId: string): string {
        const popup = this.teamScorePopupBySessionId.get(sessionId);

        if (!popup || popup.expiresAt <= Date.now()) {
            return "";
        }

        const sizeClass = popup.points >= 5 ? " is-big-award" : "";

        return `<span class="pictionary-score-popup${sizeClass}" data-score-popup-sequence="${escapeHtml(
            String(popup.sequence)
        )}" aria-hidden="true">+${escapeHtml(String(popup.points))}</span>`;
    }

    private showPlayerScoreCelebration(pointDelta: number, celebration: PictionaryCelebrationDetails) {
        if (pointDelta <= 0) {
            return;
        }

        if (this.celebrationTimerId !== null) {
            window.clearTimeout(this.celebrationTimerId);
            this.celebrationTimerId = null;
        }

        this.playerCelebration.hidden = false;
        this.playerCelebration.classList.remove("is-active");
        this.viewerNameChip.classList.remove("is-score-popping");
        const celebrationCopyClass = celebration.className ? ` ${escapeHtml(celebration.className)}` : "";
        const breakdownMarkup =
            celebration.breakdownItems.length > 0
                ? `
                    <div class="pictionary-celebration-breakdown" aria-hidden="true">
                        ${celebration.breakdownItems
                            .map(
                                (item) => `
                                    <span class="pictionary-celebration-chip is-${escapeHtml(item.tone)}">
                                        <span>${escapeHtml(item.label)}</span>
                                        <strong>+${escapeHtml(String(item.points))}</strong>
                                    </span>
                                `
                            )
                            .join("")}
                    </div>
                `
                : "";
        this.playerCelebration.innerHTML = `
            <span class="pictionary-celebration-burst" aria-hidden="true"></span>
            <div class="pictionary-celebration-card">
                <strong class="pictionary-celebration-points" aria-label="+${escapeHtml(
                    `${pointDelta} ${t("pointUnitShort")}`
                )}">
                    <span class="pictionary-celebration-plus" aria-hidden="true">+</span>
                    <span class="pictionary-celebration-number" aria-hidden="true">${escapeHtml(String(pointDelta))}</span>
                    <span class="pictionary-celebration-unit" aria-hidden="true">${escapeHtml(
                        t("pointUnitShort")
                    )}</span>
                </strong>
                <span class="pictionary-celebration-copy${celebrationCopyClass}">${escapeHtml(
                    celebration.headline
                )}</span>
                ${breakdownMarkup}
            </div>
        `;

        window.requestAnimationFrame(() => {
            this.playerCelebration.classList.add("is-active");
            this.viewerNameChip.classList.add("is-score-popping");
        });

        this.celebrationTimerId = window.setTimeout(() => {
            this.playerCelebration.hidden = true;
            this.playerCelebration.classList.remove("is-active");
            this.viewerNameChip.classList.remove("is-score-popping");
            this.celebrationTimerId = null;
        }, SITE_TUNING.pictionary.feedback.celebrationDurationMs);
    }

    private syncWorthPreviewBurst(pictionary: RoomView["pictionary"], role: RenderRole) {
        if (!pictionary) {
            this.lastObservedNextWorth = null;
            return;
        }

        const previousNextWorth = this.lastObservedNextWorth;
        this.lastObservedNextWorth = pictionary.nextWorth;

        if (
            role === "player" &&
            pictionary.nextWorthManual &&
            previousNextWorth !== null &&
            pictionary.nextWorth > previousNextWorth
        ) {
            this.showWorthPreviewBurst(pictionary.nextWorth);
        }
    }

    private showWorthPreviewBurst(pointValue: number) {
        if (this.worthBurstTimerId !== null) {
            window.clearTimeout(this.worthBurstTimerId);
            this.worthBurstTimerId = null;
        }

        const particleMarkup = Array.from({ length: 18 }, (_, index) => {
            const angle = Math.round((index / 18) * 360 + (index % 2) * 8);
            const distance = 82 + (index % 4) * 16;
            const size = 6 + (index % 3) * 2;
            const delay = (index % 5) * 18;
            const color = index % 3 === 0 ? "#fff0a6" : index % 3 === 1 ? "#7df4db" : "#ff9a73";

            return `<span
                class="pictionary-worth-burst-particle"
                style="--burst-angle: ${angle}deg; --burst-distance: ${distance}px; --burst-size: ${size}px; --burst-delay: ${delay}ms; --burst-color: ${color};"
                aria-hidden="true"
            ></span>`;
        }).join("");

        this.worthBurst.hidden = false;
        this.worthBurst.classList.remove("is-active");
        this.worthBurst.innerHTML = `
            <strong class="pictionary-worth-burst-value">${escapeHtml(formatPointCount(pointValue))}</strong>
            ${particleMarkup}
        `;

        window.requestAnimationFrame(() => {
            this.worthBurst.classList.add("is-active");
        });

        this.worthBurstTimerId = window.setTimeout(() => {
            this.worthBurst.hidden = true;
            this.worthBurst.classList.remove("is-active");
            this.worthBurstTimerId = null;
        }, 1320);
    }

    private scheduleCanvasRedraw() {
        if (this.canvasRedrawFrameId !== null) {
            return;
        }

        this.canvasRedrawFrameId = window.requestAnimationFrame(() => {
            this.canvasRedrawFrameId = null;
            this.redrawCanvas();
        });
    }

    private shouldAnimateIdleCanvas(): boolean {
        const pictionary = this.currentRoom?.pictionary;

        if (!pictionary || this.currentRole === "controller") {
            return false;
        }

        if (!pictionary.roundActive) {
            return true;
        }

        return Boolean(pictionary.roundComplete && pictionary.roundCompletedAt);
    }

    private syncIdleCanvasAnimation() {
        const shouldAnimate = this.shouldAnimateIdleCanvas();

        if (!shouldAnimate) {
            if (this.idleAnimationFrameId !== null) {
                window.cancelAnimationFrame(this.idleAnimationFrameId);
                this.idleAnimationFrameId = null;
            }
            return;
        }

        if (this.idleAnimationFrameId !== null) {
            return;
        }

        const tick = () => {
            this.idleAnimationFrameId = null;
            this.redrawCanvas();

            if (this.shouldAnimateIdleCanvas()) {
                this.idleAnimationFrameId = window.requestAnimationFrame(tick);
            }
        };

        this.idleAnimationFrameId = window.requestAnimationFrame(tick);
    }

    private syncRoundTimer() {
        const pictionary = this.currentRoom?.pictionary;

        if (
            !pictionary?.roundActive ||
            pictionary.roundComplete ||
            !pictionary.roundStartedAt ||
            PICTIONARY_ROUND_DURATION_SECONDS <= 0
        ) {
            this.roundTimer.hidden = true;
            this.roundTimer.style.removeProperty("--pictionary-round-remaining");
            this.roundTimer.removeAttribute("data-countdown");
            this.roundTimer.removeAttribute("data-countdown-pulse");
            this.roundTimer.classList.remove("has-countdown", "is-grace-countdown");
            this.roundTimerCountdownNumeral = null;
            this.roundTimerCountdownPulse = "";
            this.roundTimerCountdownVisibleUntil = 0;
            this.roundTimerCountdownRoundKey = "";
            this.lastRoundTimerCountdownSoundKey = "";
            return;
        }

        const now = this.getServerAlignedNow();
        const elapsedMs = Math.max(0, now - pictionary.roundStartedAt);
        const durationMs = PICTIONARY_ROUND_DURATION_SECONDS * 1000;
        const remainingMs = Math.max(0, durationMs - elapsedMs);
        const remaining = Math.max(0, 1 - elapsedMs / durationMs);
        const displaySeconds = Math.ceil(remainingMs / 1000);
        const autoSubmitAt = getPictionaryRoundAutoSubmitAt(pictionary.roundStartedAt);
        const inSubmitGrace = autoSubmitAt !== null && remainingMs <= 0 && now < autoSubmitAt;
        const freshCountdownNumeral = inSubmitGrace ? 0 : getPictionaryRoundTimerNumeral(remainingMs);
        const roundKey = `${pictionary.roundNumber}:${pictionary.roundStartedAt}`;

        if (this.roundTimerCountdownRoundKey && this.roundTimerCountdownRoundKey !== roundKey) {
            this.roundTimerCountdownNumeral = null;
            this.roundTimerCountdownPulse = "";
            this.roundTimerCountdownVisibleUntil = 0;
            this.roundTimerCountdownRoundKey = "";
            this.lastRoundTimerCountdownSoundKey = "";
        }

        let countdownNumeral = freshCountdownNumeral;
        let countdownPulse = freshCountdownNumeral === null ? "" : freshCountdownNumeral % 2 === 0 ? "even" : "odd";

        if (freshCountdownNumeral !== null) {
            this.roundTimerCountdownNumeral = freshCountdownNumeral;
            this.roundTimerCountdownPulse = countdownPulse;
            this.roundTimerCountdownRoundKey = roundKey;
            this.roundTimerCountdownVisibleUntil =
                freshCountdownNumeral > 0
                    ? now + ROUND_TIMER_NUMERAL_LINGER_MS
                    : inSubmitGrace
                      ? autoSubmitAt
                      : now;
        } else if (
            this.roundTimerCountdownNumeral !== null &&
            this.roundTimerCountdownNumeral > 0 &&
            this.roundTimerCountdownVisibleUntil > now &&
            this.roundTimerCountdownRoundKey === roundKey
        ) {
            countdownNumeral = this.roundTimerCountdownNumeral;
            countdownPulse = this.roundTimerCountdownPulse;
        } else {
            this.roundTimerCountdownNumeral = null;
            this.roundTimerCountdownPulse = "";
            this.roundTimerCountdownVisibleUntil = 0;
            this.roundTimerCountdownRoundKey = "";
        }

        this.roundTimer.hidden = false;
        this.roundTimer.style.setProperty("--pictionary-round-remaining", remaining.toFixed(4));
        this.roundTimer.classList.toggle("has-countdown", countdownNumeral !== null);
        this.roundTimer.classList.toggle(
            "is-grace-countdown",
            countdownNumeral !== null && displaySeconds <= PICTIONARY_ROUND_TIMER_GRACE_START_SECONDS
        );

        if (countdownNumeral === null) {
            this.roundTimer.removeAttribute("data-countdown");
            this.roundTimer.removeAttribute("data-countdown-pulse");
        } else {
            this.roundTimer.dataset.countdown = String(countdownNumeral);
            this.roundTimer.dataset.countdownPulse = countdownPulse;
        }

        if (
            freshCountdownNumeral !== null &&
            freshCountdownNumeral >= 0 &&
            freshCountdownNumeral <= 5
        ) {
            const countdownSoundKey = `${roundKey}:${freshCountdownNumeral}`;

            if (countdownSoundKey !== this.lastRoundTimerCountdownSoundKey) {
                if (freshCountdownNumeral % 2 === 1) {
                    this.audio.playCountdownTick();
                } else {
                    this.audio.playCountdownTock();
                }

                this.lastRoundTimerCountdownSoundKey = countdownSoundKey;
            }
        }

        if (
            autoSubmitAt !== null &&
            now >= autoSubmitAt &&
            this.autoEndedRoundKey !== roundKey &&
            (this.currentRole === "controller" || this.currentRole === "host-controller")
        ) {
            this.autoEndedRoundKey = roundKey;
            this.options.onEndRound();
        }
    }

    private syncGuessInputDock() {
        const focusedGuessInput = this.getFocusedGuessInput();
        const keyboardVisible = this.isMobileKeyboardVisible();
        const shouldDock =
            this.currentRole === "player" &&
            Boolean(focusedGuessInput && !focusedGuessInput.disabled) &&
            keyboardVisible;
        const wasDocked = this.guessInputDocked;

        this.guessInputDocked = shouldDock;
        this.shell.classList.toggle("is-guessing", shouldDock);

        const keyboardInset = shouldDock ? this.getMobileKeyboardDockInset() : 0;
        const visibleViewportHeight = shouldDock ? this.getKeyboardDockAvailableHeight() : 0;
        this.shell.classList.toggle(
            "is-keyboard-short",
            shouldDock && visibleViewportHeight <= SHORT_KEYBOARD_VIEWPORT_MAX_HEIGHT_PX
        );
        this.root.style.setProperty("--pictionary-keyboard-inset", `${Math.round(keyboardInset)}px`);
        if (shouldDock) {
            this.root.style.setProperty(
                "--pictionary-visual-viewport-height",
                `${Math.round(Math.max(0, visibleViewportHeight))}px`
            );
        } else {
            this.root.style.removeProperty("--pictionary-visual-viewport-height");
        }

        this.syncGuessSuggestionPadding();

        if (shouldDock || wasDocked) {
            this.restorePageScrollTopSoon();
        }

        if (shouldDock) {
            this.syncCapyPeekVisibilityGuard();
        }
    }

    private getKeyboardDockAvailableHeight(): number {
        const visualViewport = window.visualViewport;
        const visualViewportHeight = visualViewport?.height ?? window.innerHeight;
        const visualViewportTop = visualViewport?.offsetTop ?? 0;
        const rootTop = this.root.getBoundingClientRect().top;

        return Math.max(0, visualViewportHeight - Math.max(0, rootTop - visualViewportTop));
    }

    private isKeyboardDockViewport(): boolean {
        if (window.matchMedia("(max-width: 640px)").matches) {
            return true;
        }

        const touchCapable = window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;

        return touchCapable && window.matchMedia("(max-width: 1368px), (max-height: 1024px)").matches;
    }

    private getMobileKeyboardInset(): number {
        const visualViewport = window.visualViewport;

        if (!visualViewport || !this.isKeyboardDockViewport()) {
            return 0;
        }

        return Math.max(0, window.innerHeight - visualViewport.height - visualViewport.offsetTop);
    }

    private getMobileKeyboardDockInset(): number {
        return Math.min(KEYBOARD_LIFT_MAX_INSET_PX, this.getMobileKeyboardInset() * KEYBOARD_LIFT_INSET_RATIO);
    }

    private isMobileKeyboardVisible(): boolean {
        if (!this.isKeyboardDockViewport()) {
            return false;
        }

        if (!window.visualViewport) {
            return true;
        }

        return this.getMobileKeyboardInset() >= MOBILE_GUESS_KEYBOARD_MIN_INSET_PX;
    }

    private getFocusedGuessInput(): HTMLInputElement | null {
        const activeElement = document.activeElement;

        if (
            activeElement instanceof HTMLInputElement &&
            activeElement.id === "pictionary-guess-input" &&
            this.consoleSlot.contains(activeElement)
        ) {
            return activeElement;
        }

        return null;
    }

    private restorePageScrollTop() {
        window.scrollTo({
            left: window.scrollX,
            top: 0
        });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }

    private restorePageScrollTopSoon() {
        this.restorePageScrollTop();
        window.requestAnimationFrame(() => this.restorePageScrollTop());

        if (this.guessInputScrollRestoreTimerId !== null) {
            window.clearTimeout(this.guessInputScrollRestoreTimerId);
        }

        this.guessInputScrollRestoreTimerId = window.setTimeout(() => {
            this.guessInputScrollRestoreTimerId = null;
            this.restorePageScrollTop();
        }, KEYBOARD_SCROLL_RESTORE_DELAY_MS);
    }

    private rerenderRoleSlot(preserveEntryGridScroll = false) {
        const entryGrid = preserveEntryGridScroll
            ? this.roleSlot.querySelector<HTMLElement>(".pictionary-entry-grid")
            : null;
        const previousScrollTop = entryGrid?.scrollTop ?? 0;
        const previousScrollY = preserveEntryGridScroll ? window.scrollY : 0;

        const markup = this.renderRoleMarkup(this.currentRoom!, this.currentRole);
        const reviewScrollTop = this.roleSlot.querySelector<HTMLElement>(".pictionary-word-bank-table-shell")?.scrollTop ?? 0;
        replaceReviewMarkup(this.roleSlot, markup);
        const reviewScroller = this.roleSlot.querySelector<HTMLElement>(".pictionary-word-bank-table-shell");
        if (reviewScroller) reviewScroller.scrollTop = reviewScrollTop;
        this.roleMarkupCache = markup;

        if (preserveEntryGridScroll) {
            const nextEntryGrid = this.roleSlot.querySelector<HTMLElement>(".pictionary-entry-grid");

            if (nextEntryGrid) {
                nextEntryGrid.scrollTop = previousScrollTop;
            }

            window.scrollTo({
                left: window.scrollX,
                top: previousScrollY
            });
        }
    }

    private setRoundAnswerMarkup(markup: string) {
        this.roundAnswerValue.hidden = markup.length === 0;
        if (markup === this.currentAnswerMarkup) return;
        this.currentAnswerMarkup = markup;
        this.roundAnswerValue.innerHTML = markup;
    }

    private setStemMarkup(markup: string) {
        // Timer and room updates must not restart the new-clue entrance animation.
        if (markup === this.currentStemMarkup) return;
        this.currentStemMarkup = markup;
        const template = document.createElement("template");
        template.innerHTML = markup;
        const fresh = Array.from(template.content.children) as HTMLElement[];
        const retained = Array.from(this.stemValue.children) as HTMLElement[];
        const arrivals: HTMLElement[] = [];
        if (fresh.length !== retained.length) {
            this.stemValue.replaceChildren(template.content);
            arrivals.push(...fresh.filter(element => element.classList.contains("is-new")));
        } else {
            // Revealing a later clue must not remount or replay earlier clues.
            fresh.forEach((next, index) => {
                const current = retained[index]!;
                if (next.classList.contains("is-new") && !current.classList.contains("is-new")) arrivals.push(current);
                if (current.className !== next.className) current.className = next.className;
                if (current.innerHTML !== next.innerHTML) current.innerHTML = next.innerHTML;
            });
        }
        if (!markup) this.clueMotion.cancel();
        else if (arrivals.length) this.clueMotion.play(arrivals, "clue", [
            { opacity: 0.35, transform: "translateY(-0.25em)" },
            { opacity: 1, transform: "translateY(0)" }
        ], SITE_TUNING.pictionary.motion.clueEnterMs);
    }

    private refreshStageStemMarkup() {
        const pictionary = this.currentRoom?.pictionary;

        if (!pictionary || !pictionary.roundActive) {
            this.clueMotion.cancel();
            this.stemValue.classList.remove("is-long", "is-very-long");
            this.currentRevealRoundKey = "";
            this.lastRevealCount = 0;
            this.latestRevealIndices.clear();
            this.latestRevealPulseUntil = 0;
            this.stemValue.hidden = true;
            this.setStemMarkup("");
            this.setRoundAnswerMarkup("");
            return;
        }

        if (this.currentRole === "controller" || this.currentRole === "host-controller") {
            this.clueMotion.cancel();
            this.stemValue.classList.remove("is-long", "is-very-long");
            this.currentRevealRoundKey = "";
            this.lastRevealCount = 0;
            this.latestRevealIndices.clear();
            this.latestRevealPulseUntil = 0;
            this.stemValue.hidden = pictionary.stem.length === 0;
            this.setStemMarkup(renderStaticStemSegment(pictionary.stem));
            this.setRoundAnswerMarkup("");
            return;
        }

        const answerMarkup = renderRoundAnswerMarkup(pictionary);
        this.setRoundAnswerMarkup(answerMarkup);

        if (pictionary.noHints || pictionary.hintMode === "off") {
            this.clueMotion.cancel();
            this.stemValue.classList.remove("is-long", "is-very-long");
            this.currentRevealRoundKey = "";
            this.lastRevealCount = 0;
            this.latestRevealIndices.clear();
            this.latestRevealPulseUntil = 0;
            this.stemValue.hidden = true;
            this.setStemMarkup("");
            return;
        }

        const revealRoundKey = `${this.currentRoom?.roomCode ?? ""}:${pictionary.currentWordIndex ?? "none"}:${
            pictionary.roundStartedAt ?? "none"
        }`;
        const revealCount = pictionary.roundComplete
            ? pictionary.stemRevealOrder.length
            : getPictionaryStemRevealCount(
                  pictionary.stemRevealOrder,
                  pictionary.roundStartedAt,
                  pictionary.revealDurationSeconds,
                  pictionary.hintMode,
                  this.getServerAlignedNow()
              );

        if (revealRoundKey !== this.currentRevealRoundKey) {
            this.clueMotion.cancel();
            this.currentRevealRoundKey = revealRoundKey;
            this.lastRevealCount = revealCount;
            this.latestRevealIndices.clear();
            this.latestRevealPulseUntil = 0;
        } else if (revealCount > this.lastRevealCount) {
            this.latestRevealIndices = new Set(
                pictionary.stemRevealOrder
                    .slice(this.lastRevealCount, revealCount)
                    .filter((index): index is number => typeof index === "number")
            );
            this.latestRevealPulseUntil = Date.now() + 1900;
            this.lastRevealCount = revealCount;
        }

        const segments = buildPictionaryStemRevealSegments(
            pictionary.guessStem,
            pictionary.stemRevealOrder,
            revealCount
        );
        const shouldPulse = this.latestRevealIndices.size > 0 && this.latestRevealPulseUntil > Date.now();
        const stemLength = Array.from(pictionary.guessStem).length;

        this.stemValue.classList.toggle("is-long", stemLength > 58);
        this.stemValue.classList.toggle("is-very-long", stemLength > 92);

        this.stemValue.hidden = segments.every((segment) => segment.text.length === 0);
        const stemMarkup = segments
            .reduce<string[]>((parts, segment) => {
                if (segment.revealableIndex === null) {
                    if (parts.length > 0 && STEM_TRAILING_PUNCTUATION_PATTERN.test(segment.text)) {
                        parts[parts.length - 1] += renderStaticStemSegmentWithPunctuationGlue(segment.text);
                    } else {
                        parts.push(renderStaticStemSegment(segment.text));
                    }

                    return parts;
                }

                parts.push(`<span class="pictionary-stem-segment ${segment.revealed ? "is-revealed" : "is-hidden"} ${
                    shouldPulse && this.latestRevealIndices.has(segment.revealableIndex) ? "is-new" : ""
                }">${escapeHtml(segment.text)}</span>`);
                return parts;
            }, [])
            .join("");
        this.setStemMarkup(stemMarkup);
    }

    private getSmoothedCanvasPoints(
        points: PictionaryCanvasPoint[],
        width: number,
        height: number
    ): Array<{ x: number; y: number }> {
        const canvasPoints = points.map((point) => ({
            x: point.x * width,
            y: point.y * height
        }));

        if (canvasPoints.length <= 2 || SMOOTHING_WEIGHT <= 0) {
            return canvasPoints;
        }

        const smoothedPoints = canvasPoints.map((point, index) => {
            if (index === 0 || index === canvasPoints.length - 1) {
                return point;
            }

            const previousPoint = canvasPoints[index - 1]!;
            const nextPoint = canvasPoints[index + 1]!;
            const ownWeight = 1 - SMOOTHING_WEIGHT * 2;

            return {
                x: point.x * ownWeight + (previousPoint.x + nextPoint.x) * SMOOTHING_WEIGHT,
                y: point.y * ownWeight + (previousPoint.y + nextPoint.y) * SMOOTHING_WEIGHT
            };
        });

        return smoothedPoints;
    }

    private drawStroke(
        context: CanvasRenderingContext2D,
        stroke: PictionaryStrokeView,
        width: number,
        height: number
    ) {
        if (stroke.points.length === 0) {
            return;
        }

        if (stroke.points.length === 1) {
            const point = stroke.points[0];

            if (!point) {
                return;
            }

            context.beginPath();
            context.arc(point.x * width, point.y * height, context.lineWidth / 2, 0, Math.PI * 2);
            context.fillStyle = "#101014";
            context.fill();
            return;
        }

        const canvasPoints = this.getSmoothedCanvasPoints(stroke.points, width, height);

        context.beginPath();
        context.moveTo(canvasPoints[0]!.x, canvasPoints[0]!.y);

        if (canvasPoints.length === 2) {
            context.lineTo(canvasPoints[1]!.x, canvasPoints[1]!.y);
            context.stroke();
            return;
        }

        for (let index = 1; index < canvasPoints.length - 1; index += 1) {
            const point = canvasPoints[index]!;
            const nextPoint = canvasPoints[index + 1]!;
            const midpointX = (point.x + nextPoint.x) / 2;
            const midpointY = (point.y + nextPoint.y) / 2;

            context.quadraticCurveTo(point.x, point.y, midpointX, midpointY);
        }

        const lastPoint = canvasPoints[canvasPoints.length - 1]!;
        context.lineTo(lastPoint.x, lastPoint.y);

        context.stroke();
    }

    private drawIdlePaperSlip(
        context: CanvasRenderingContext2D,
        cardWidth: number,
        cardHeight: number,
        accent: string,
        opacity: number,
        shortSide: number
    ) {
        const cornerSize = cardWidth * 0.16;
        const radius = cardWidth * 0.07;

        context.fillStyle = `rgba(255, 255, 250, ${0.72 * opacity})`;
        context.strokeStyle = `rgba(${accent}, ${0.38 * opacity})`;
        context.lineWidth = Math.max(1, shortSide * 0.0021);

        context.beginPath();
        context.moveTo(-cardWidth / 2 + radius, -cardHeight / 2);
        context.lineTo(cardWidth / 2 - cornerSize, -cardHeight / 2);
        context.lineTo(cardWidth / 2, -cardHeight / 2 + cornerSize);
        context.lineTo(cardWidth / 2, cardHeight / 2 - radius);
        context.quadraticCurveTo(cardWidth / 2, cardHeight / 2, cardWidth / 2 - radius, cardHeight / 2);
        context.lineTo(-cardWidth / 2 + radius, cardHeight / 2);
        context.quadraticCurveTo(-cardWidth / 2, cardHeight / 2, -cardWidth / 2, cardHeight / 2 - radius);
        context.lineTo(-cardWidth / 2, -cardHeight / 2 + radius);
        context.quadraticCurveTo(-cardWidth / 2, -cardHeight / 2, -cardWidth / 2 + radius, -cardHeight / 2);
        context.closePath();
        context.fill();
        context.stroke();

        context.strokeStyle = `rgba(${accent}, ${0.22 * opacity})`;
        context.lineWidth = Math.max(1, shortSide * 0.0017);
        context.beginPath();
        context.moveTo(cardWidth / 2 - cornerSize, -cardHeight / 2);
        context.lineTo(cardWidth / 2 - cornerSize * 0.92, -cardHeight / 2 + cornerSize * 0.92);
        context.lineTo(cardWidth / 2, -cardHeight / 2 + cornerSize);
        context.stroke();

        context.strokeStyle = `rgba(${accent}, ${0.18 * opacity})`;
        context.lineWidth = Math.max(1, shortSide * 0.0016);
        context.beginPath();
        context.moveTo(-cardWidth * 0.28, -cardHeight * 0.34);
        context.lineTo(-cardWidth * 0.28, cardHeight * 0.34);
        context.stroke();

        context.strokeStyle = `rgba(${accent}, ${0.34 * opacity})`;
        context.lineWidth = Math.max(1, shortSide * 0.0019);
        context.beginPath();
        context.moveTo(-cardWidth * 0.18, -cardHeight * 0.29);
        context.lineTo(cardWidth * 0.26, -cardHeight * 0.29);
        context.stroke();

        context.strokeStyle = `rgba(${accent}, ${0.28 * opacity})`;
        context.lineWidth = Math.max(1, shortSide * 0.0018);
        for (let rowIndex = 0; rowIndex < 4; rowIndex += 1) {
            const lineY = -cardHeight * 0.12 + rowIndex * cardHeight * 0.145;
            context.beginPath();
            context.moveTo(-cardWidth * 0.22, lineY);
            context.lineTo(cardWidth * (rowIndex === 3 ? 0.16 : 0.31), lineY);
            context.stroke();
        }
    }

    private drawIdleNotebookFragment(
        context: CanvasRenderingContext2D,
        pageWidth: number,
        pageHeight: number,
        accent: string,
        opacity: number,
        shortSide: number
    ) {
        const cornerSize = pageWidth * 0.18;
        const radius = pageWidth * 0.08;

        context.fillStyle = `rgba(255, 255, 250, ${0.52 * opacity})`;
        context.strokeStyle = `rgba(${accent}, ${0.28 * opacity})`;
        context.lineWidth = Math.max(1, shortSide * 0.0016);
        context.lineJoin = "round";

        context.beginPath();
        context.moveTo(-pageWidth / 2 + radius, -pageHeight / 2);
        context.lineTo(pageWidth / 2 - cornerSize, -pageHeight / 2);
        context.lineTo(pageWidth / 2, -pageHeight / 2 + cornerSize);
        context.lineTo(pageWidth / 2, pageHeight / 2 - radius);
        context.quadraticCurveTo(pageWidth / 2, pageHeight / 2, pageWidth / 2 - radius, pageHeight / 2);
        context.lineTo(-pageWidth / 2 + radius, pageHeight / 2);
        context.quadraticCurveTo(-pageWidth / 2, pageHeight / 2, -pageWidth / 2, pageHeight / 2 - radius);
        context.lineTo(-pageWidth / 2, -pageHeight / 2 + radius);
        context.quadraticCurveTo(-pageWidth / 2, -pageHeight / 2, -pageWidth / 2 + radius, -pageHeight / 2);
        context.closePath();
        context.fill();
        context.stroke();

        context.strokeStyle = `rgba(${accent}, ${0.2 * opacity})`;
        context.lineWidth = Math.max(1, shortSide * 0.0013);
        context.beginPath();
        context.moveTo(-pageWidth * 0.24, -pageHeight * 0.3);
        context.lineTo(-pageWidth * 0.24, pageHeight * 0.3);
        context.stroke();

        context.strokeStyle = `rgba(${accent}, ${0.26 * opacity})`;
        for (let lineIndex = 0; lineIndex < 3; lineIndex += 1) {
            const lineY = -pageHeight * 0.18 + lineIndex * pageHeight * 0.18;
            context.beginPath();
            context.moveTo(-pageWidth * 0.15, lineY);
            context.lineTo(pageWidth * (lineIndex === 2 ? 0.12 : 0.28), lineY);
            context.stroke();
        }
    }

    private drawIdleScribbleFlake(
        context: CanvasRenderingContext2D,
        motif: number,
        size: number,
        accent: string,
        opacity: number,
        shortSide: number
    ) {
        context.strokeStyle = `rgba(${accent}, ${opacity})`;
        context.fillStyle = `rgba(${accent}, ${opacity * 0.78})`;
        context.lineWidth = Math.max(1, shortSide * 0.002);
        context.lineCap = "round";
        context.lineJoin = "round";

        if (motif === 0) {
            context.beginPath();
            context.moveTo(-size * 0.42, -size * 0.06);
            context.quadraticCurveTo(-size * 0.08, -size * 0.44, size * 0.3, -size * 0.14);
            context.quadraticCurveTo(size * 0.5, size * 0.04, size * 0.12, size * 0.32);
            context.stroke();
            return;
        }

        if (motif === 1) {
            context.beginPath();
            context.moveTo(-size * 0.44, -size * 0.2);
            context.lineTo(size * 0.36, size * 0.18);
            context.moveTo(-size * 0.28, size * 0.28);
            context.lineTo(size * 0.22, -size * 0.28);
            context.stroke();
            return;
        }

        if (motif === 2) {
            context.beginPath();
            context.moveTo(-size * 0.42, -size * 0.22);
            context.lineTo(size * 0.18, -size * 0.36);
            context.moveTo(-size * 0.24, 0);
            context.lineTo(size * 0.36, -size * 0.12);
            context.moveTo(-size * 0.36, size * 0.24);
            context.lineTo(size * 0.12, size * 0.14);
            context.stroke();
            return;
        }

        if (motif === 3) {
            context.beginPath();
            context.arc(-size * 0.14, -size * 0.12, size * 0.2, 0, Math.PI * 2);
            context.stroke();
            context.beginPath();
            context.moveTo(size * 0.02, size * 0.04);
            context.lineTo(size * 0.34, size * 0.36);
            context.stroke();
            return;
        }

        context.beginPath();
        context.moveTo(-size * 0.42, size * 0.18);
        context.quadraticCurveTo(-size * 0.12, size * 0.34, size * 0.32, size * 0.12);
        context.stroke();
        context.beginPath();
        context.moveTo(-size * 0.12, -size * 0.26);
        context.lineTo(size * 0.22, -size * 0.38);
        context.stroke();
    }

    private drawIdleCanvasDecor(
        context: CanvasRenderingContext2D,
        width: number,
        height: number,
        prompt: string,
        opacity = 1,
        promptOpacity = opacity
    ) {
        const now = Date.now() * 0.001;
        const shortSide = Math.min(width, height);
        const cardWidth = Math.max(54, Math.min(width * 0.13, shortSide * 0.24));
        const cardHeight = cardWidth * 0.76;
        const palette = [
            "45, 52, 56",
            "66, 62, 55",
            "84, 78, 68",
            "112, 88, 45"
        ];

        context.save();

        for (let cardIndex = 0; cardIndex < 5; cardIndex += 1) {
            const seed = cardIndex * 1.73 + 0.41;
            const progress = (now * (0.026 + (cardIndex % 3) * 0.005) + cardIndex / 5) % 1;
            const lane = ((cardIndex * 3) % 5) / 4;
            const drift = Math.sin(progress * Math.PI * 2 + seed) * shortSide * 0.045;
            const x = width * (0.17 + lane * 0.66) + drift;
            const y = -cardHeight + progress * (height * 0.56 + cardHeight * 2.4);
            const rotation = Math.sin(now * 0.2 + seed) * 0.11 + (lane - 0.5) * 0.16;
            const scale = 0.72 + (cardIndex % 3) * 0.07;
            const cardOpacity = (0.34 + Math.sin(progress * Math.PI) * 0.38) * opacity;
            const accent = palette[cardIndex % palette.length];

            context.save();
            context.translate(x, y);
            context.rotate(rotation);
            context.scale(scale, scale);
            this.drawIdlePaperSlip(context, cardWidth, cardHeight, accent, cardOpacity, shortSide);
            context.restore();
        }

        for (let particleIndex = 0; particleIndex < IDLE_PARTICLE_COUNT; particleIndex += 1) {
            const seed = particleIndex * 1.913 + 0.28;
            const progress =
                (now * (0.044 + (particleIndex % 5) * 0.006) + particleIndex / IDLE_PARTICLE_COUNT) % 1;
            const lane = ((particleIndex * 11) % IDLE_PARTICLE_COUNT) / Math.max(1, IDLE_PARTICLE_COUNT - 1);
            const horizontalDrift =
                Math.sin(progress * Math.PI * 2 + seed) * shortSide * 0.048 +
                Math.sin(now * 0.18 + seed) * shortSide * 0.018;
            const x = width * (0.12 + lane * 0.76) + horizontalDrift;
            const fallPadding = shortSide * 0.13;
            const y = -fallPadding + progress * (height + fallPadding * 2);
            const size = Math.max(12, shortSide * (0.022 + (particleIndex % 4) * 0.0045));
            const angle = Math.sin(now * 0.28 + seed) * 0.48 + (progress - 0.5) * 0.32;
            const accent = palette[particleIndex % palette.length];
            const edgeFade = Math.min(1, Math.max(0, Math.sin(progress * Math.PI) * 1.35));
            const alpha = (0.22 + (particleIndex % 4) * 0.028) * edgeFade;

            context.save();
            context.translate(x, y);
            context.rotate(angle);
            this.drawIdleScribbleFlake(context, particleIndex % 5, size, accent, alpha * opacity, shortSide);
            context.restore();
        }

        for (let fragmentIndex = 0; fragmentIndex < 8; fragmentIndex += 1) {
            const seed = fragmentIndex * 1.47 + 0.9;
            const progress = (now * (0.035 + (fragmentIndex % 3) * 0.008) + fragmentIndex / 8) % 1;
            const x =
                width * (0.15 + (((fragmentIndex * 5) % 8) / 7) * 0.7) +
                Math.cos(progress * Math.PI * 2 + seed) * shortSide * 0.035;
            const y = -shortSide * 0.08 + progress * (height + shortSide * 0.16);
            const pageWidth = Math.max(14, shortSide * (0.021 + (fragmentIndex % 2) * 0.004));
            const pageHeight = pageWidth * 0.72;
            const alpha = Math.min(0.26, Math.sin(progress * Math.PI) * 0.22) * opacity;

            context.save();
            context.translate(x, y);
            context.rotate(Math.sin(now * 0.22 + seed) * 0.38 + (progress - 0.5) * 0.24);
            this.drawIdleNotebookFragment(
                context,
                pageWidth,
                pageHeight,
                palette[(fragmentIndex + 1) % palette.length],
                alpha,
                shortSide
            );
            context.restore();
        }

        context.restore();
        if (promptOpacity > 0.01) {
            context.save();
            context.fillStyle = `rgba(55, 50, 43, ${0.86 * promptOpacity})`;
            context.font = `${Math.max(18, Math.round(width * 0.034))}px "Trebuchet MS", "Avenir Next", sans-serif`;
            context.textAlign = "center";
            context.textBaseline = "middle";
            context.fillText(prompt, width / 2, height / 2);
            context.restore();
        }
    }

    private getCurrentGuessRiskCount(room: RoomView): number {
        const pictionary = room.pictionary;
        const viewer = pictionary?.viewer;

        if (
            !pictionary ||
            !viewer ||
            !pictionary.roundActive ||
            pictionary.roundComplete ||
            viewer.solved ||
            viewer.livesRemaining <= 0 ||
            !this.canViewerGuessNow(pictionary) ||
            Boolean(viewer.cooldownEndsAt && viewer.cooldownEndsAt > this.getServerAlignedNow())
        ) {
            return 0;
        }

        return clampRiskedTryCount(
            viewer.livesRemaining,
            getPictionaryGuessPenaltyLives(pictionary.roundStartedAt, this.getServerAlignedNow())
        );
    }

    private getViewerObserveCountdown(
        pictionary: NonNullable<RoomView["pictionary"]>
    ): string {
        return formatCountdownSeconds(pictionary.viewer?.observeEndsAt ?? null, this.getServerAlignedNow());
    }

    private canViewerGuessNow(pictionary: RoomView["pictionary"]): boolean {
        const viewer = pictionary?.viewer;

        if (!pictionary || !viewer || !pictionary.roundActive || pictionary.roundComplete || viewer.solved) {
            return false;
        }

        const now = this.getServerAlignedNow();

        if (viewer.observeEndsAt && viewer.observeEndsAt > now) {
            return false;
        }

        if (viewer.cooldownEndsAt && viewer.cooldownEndsAt > now) {
            return false;
        }

        return true;
    }

    private getGuessPlaceholder(pictionary: NonNullable<RoomView["pictionary"]>): string {
        return this.getViewerObserveCountdown(pictionary) ? t("observeFirst") : t("guessPlaceholder");
    }

    private redrawCanvas() {
        const context = this.canvas.getContext("2d");

        if (!context) {
            return;
        }

        const width = this.canvas.width;
        const height = this.canvas.height;
        const devicePixelRatio = window.devicePixelRatio || 1;
        const visibleDrawingHeight =
            this.getCanvasVisibleDrawingHeight(width / devicePixelRatio, height / devicePixelRatio) *
            devicePixelRatio;
        const pictionary = this.currentRoom?.pictionary;
        const roundCompletedAge =
            pictionary?.roundComplete && this.roundCompleteVisualStartedAt !== null
                ? Date.now() - this.roundCompleteVisualStartedAt
                : 0;
        const isRoundCompleteTransition = Boolean(pictionary?.roundComplete);
        const shouldShowRoundCompletePrompt =
            isRoundCompleteTransition && roundCompletedAge >= ROUND_COMPLETE_PROMPT_DELAY_MS;
        const roundCompleteFadeRawProgress = shouldShowRoundCompletePrompt
            ? ROUND_COMPLETE_PROMPT_FADE_MS > 0
                ? clamp01((roundCompletedAge - ROUND_COMPLETE_PROMPT_DELAY_MS) / ROUND_COMPLETE_PROMPT_FADE_MS)
                : 1
            : 0;
        const roundCompleteFadeProgress = easeInOutCubic(roundCompleteFadeRawProgress);
        const roundCompleteDecorProgress = isRoundCompleteTransition ? roundCompleteFadeProgress : 1;
        const shouldShowIdleDecor = !pictionary?.roundActive || (isRoundCompleteTransition && roundCompleteDecorProgress > 0);

        context.clearRect(0, 0, width, height);
        context.fillStyle = !pictionary?.roundActive || roundCompleteFadeProgress > 0.5 ? "#fbfbff" : "#ffffff";
        context.fillRect(0, 0, width, visibleDrawingHeight);
        context.strokeStyle = "#101014";
        context.lineWidth = BRUSH_WIDTH * (window.devicePixelRatio || 1);
        context.lineCap = "round";
        context.lineJoin = "round";

        for (const stroke of this.strokes) {
            this.drawStroke(context, stroke, width, visibleDrawingHeight);
        }

        if (shouldShowRoundCompletePrompt && roundCompleteFadeProgress > 0) {
            context.fillStyle = `rgba(251, 251, 255, ${Math.min(0.92, roundCompleteFadeProgress * 0.88)})`;
            context.fillRect(0, 0, width, height);
        }

        if (shouldShowIdleDecor) {
            this.drawIdleCanvasDecor(
                context,
                width,
                height,
                pictionary ? getPictionaryWaitingPrompt(pictionary) : t("waitingForWord"),
                pictionary?.roundActive ? roundCompleteDecorProgress : 1,
                pictionary?.roundActive ? roundCompleteFadeProgress : 1
            );
        }
    }

    private updateCountdowns() {
        if (!this.currentRoom?.pictionary) {
            return;
        }

        this.syncCaseTranslations();
        this.refreshStageStemMarkup();
        this.syncRoundTimer();
        this.syncGuessInputDock();
        this.syncIdleCanvasAnimation();

        const viewer = this.currentRoom.pictionary.viewer;
        const now = this.getServerAlignedNow();
        const viewerCooldown = formatCountdownSeconds(viewer?.cooldownEndsAt ?? null, now);
        const cooldownLabel = this.consoleSlot.querySelector<HTMLElement>("[data-viewer-cooldown]");
        const guessButton = this.consoleSlot.querySelector<HTMLButtonElement>("[data-action=\"guess-submit\"]");
        const guessInput = this.consoleSlot.querySelector<HTMLInputElement>("#pictionary-guess-input");
        const guessRiskLabel = this.consoleSlot.querySelector<HTMLElement>("[data-guess-risk]");
        const lifeStrip = this.consoleSlot.querySelector<HTMLElement>(".pictionary-life-strip");
        const controllerCountdown = this.roleSlot.querySelector<HTMLElement>("[data-controller-release-countdown]");
        const controllerReleaseLabel = formatCountdownSeconds(this.currentRoom.pictionary.controllerReleaseAt, now);
        const currentRiskCount = this.getCurrentGuessRiskCount(this.currentRoom);
        const observeCountdown = this.currentRoom.pictionary.viewer
            ? this.getViewerObserveCountdown(this.currentRoom.pictionary)
            : "";
        const viewerCanGuessNow = this.canViewerGuessNow(this.currentRoom.pictionary);
        const tryLossAnimation = this.getActiveTryLossAnimation();
        const displayedRiskCount = currentRiskCount > 0 ? currentRiskCount : tryLossAnimation?.lostCount ?? 0;

        if (cooldownLabel) {
            cooldownLabel.textContent = viewerCooldown ? `${t("waiting")} ${viewerCooldown}` : "";
            cooldownLabel.hidden = cooldownLabel.textContent.length === 0;
        }

        if (guessButton && viewer) {
            if (viewer.solved) {
                guessButton.disabled = true;
                guessButton.textContent = t("guessed");
            } else if (observeCountdown) {
                guessButton.disabled = true;
                guessButton.textContent = t("observeCountdown", { seconds: observeCountdown });
            } else if (viewerCooldown) {
                guessButton.disabled = true;
                guessButton.textContent = `${t("waiting")} ${viewerCooldown}`;
            } else {
                guessButton.disabled = !viewerCanGuessNow;
                guessButton.textContent = t("guess");
            }
            guessButton.dataset.compactLabel = viewer.solved ? "✓" : observeCountdown || viewerCooldown || "→";
            guessButton.setAttribute("aria-label", guessButton.textContent ?? t("guess"));
        }

        if (guessInput && viewer) {
            guessInput.disabled = !viewerCanGuessNow;
            guessInput.placeholder = observeCountdown ? t("observeFirst") : t("guessPlaceholder");
        }

        this.syncGuessSuggestions();

        if (controllerCountdown) {
            controllerCountdown.textContent = controllerReleaseLabel
                ? `${t("waiting")} ${controllerReleaseLabel}`
                : "";
            controllerCountdown.hidden = controllerCountdown.textContent.length === 0;
        }

        if (guessRiskLabel) {
            guessRiskLabel.hidden = false;
            guessRiskLabel.textContent = displayedRiskCount > 0 ? `-${displayedRiskCount}` : "";
            guessRiskLabel.classList.toggle("is-empty", displayedRiskCount <= 0);
            guessRiskLabel.setAttribute("aria-hidden", displayedRiskCount > 0 ? "false" : "true");
        }

        if (lifeStrip && viewer) {
            const tryTokens = Array.from(lifeStrip.querySelectorAll<HTMLElement>(".pictionary-life-token"));
            const firstRiskedIndex = Math.max(0, viewer.livesRemaining - currentRiskCount);

            tryTokens.forEach((tryToken, index) => {
                const isFilled = index < viewer.livesRemaining;
                tryToken.classList.toggle("is-empty", !isFilled);
                tryToken.classList.toggle("is-risked", isFilled && index >= firstRiskedIndex);
            });
            lifeStrip.setAttribute("aria-label", formatLivesRemaining(viewer.livesRemaining));
        }

        this.syncCapyPeekVisibilityGuard();
    }

    private getCapyPeekContext(room: RoomView): { key: string; delayMs: number } | null {
        const pictionary = room.pictionary;

        if (this.currentRole !== "player" || !pictionary || pictionary.roundComplete) {
            return null;
        }

        if (!pictionary.roundActive) {
            return {
                key: `${room.roomCode}:initial`,
                delayMs: PICTIONARY_CAPY_INITIAL_PEEK_DELAY_MS
            };
        }

        const roundStartedAt = pictionary.roundStartedAt;
        const roundKey = `${room.roomCode}:round:${pictionary.roundNumber}:${pictionary.currentWordIndex ?? "none"}:${roundStartedAt ?? "none"}`;
        const delayMs = roundStartedAt
            ? Math.max(0, roundStartedAt + PICTIONARY_CAPY_ROUND_PEEK_DELAY_MS - this.getServerAlignedNow())
            : PICTIONARY_CAPY_ROUND_PEEK_DELAY_MS;

        return { key: roundKey, delayMs };
    }

    private isCapyPeekAllowed(): boolean {
        const pictionary = this.currentRoom?.pictionary;

        return Boolean(
            this.currentRole === "player" &&
                pictionary &&
                !pictionary.roundComplete &&
                !document.hidden &&
                !this.guessInputDocked &&
                !this.getFocusedGuessInput() &&
                this.roundTimerCountdownNumeral === null &&
                !this.roundTimer.classList.contains("has-countdown") &&
                this.playerCelebration.hidden &&
                this.worthBurst.hidden
        );
    }

    private hashCapyPeekAnchor(value: string): number {
        let hash = 0;

        for (let index = 0; index < value.length; index += 1) {
            hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
        }

        return hash;
    }

    private syncCapyPeekSchedule(room: RoomView) {
        setCapyShieldHue(this.capyPeekShield, room.viewer.avatarHue);
        const context = this.getCapyPeekContext(room);

        if (!context) {
            this.capyPeekContextKey = "";
            this.capyPeekAppearances = 0;
            this.clearCapyPeekTimers();
            this.hideCapyPeekImmediately();
            return;
        }

        if (context.key !== this.capyPeekContextKey) {
            this.clearCapyPeekTimers();
            this.hideCapyPeekImmediately();
            this.capyPeekContextKey = context.key;
            this.capyPeekAppearances = 0;
            this.capyPeekAnchorIndex =
                this.hashCapyPeekAnchor(`${context.key}:${room.viewer.sessionId}`) % PICTIONARY_CAPY_ANCHORS.length;
            this.scheduleCapyPeek(context.delayMs);
            return;
        }

        if (
            this.capyPeekButton.hidden &&
            this.capyPeekScheduleTimerId === null &&
            this.capyPeekTransitionTimerId === null &&
            this.capyPeekAppearances < PICTIONARY_CAPY_MAX_APPEARANCES
        ) {
            this.scheduleCapyPeek(PICTIONARY_CAPY_GUARD_RETRY_MS);
        }

        this.syncCapyPeekVisibilityGuard();
    }

    private scheduleCapyPeek(delayMs: number) {
        if (
            !this.capyPeekContextKey ||
            this.capyPeekScheduleTimerId !== null ||
            this.capyPeekAppearances >= PICTIONARY_CAPY_MAX_APPEARANCES
        ) {
            return;
        }

        const scheduledContextKey = this.capyPeekContextKey;
        this.capyPeekScheduleTimerId = window.setTimeout(() => {
            this.capyPeekScheduleTimerId = null;

            if (scheduledContextKey !== this.capyPeekContextKey) {
                return;
            }

            if (!this.isCapyPeekAllowed()) {
                this.scheduleCapyPeek(PICTIONARY_CAPY_GUARD_RETRY_MS);
                return;
            }

            this.showCapyPeek();
        }, Math.max(0, delayMs));
    }

    private showCapyPeek() {
        if (!this.isCapyPeekAllowed()) {
            this.scheduleCapyPeek(PICTIONARY_CAPY_GUARD_RETRY_MS);
            return;
        }

        const anchor: PictionaryCapyAnchor = PICTIONARY_CAPY_ANCHORS[this.capyPeekAnchorIndex];
        this.capyPeekAnchorIndex = (this.capyPeekAnchorIndex + 1) % PICTIONARY_CAPY_ANCHORS.length;
        this.capyPeekAppearances += 1;
        this.capyPeekButton.dataset.capyAnchor = anchor;
        this.capyPeekButton.classList.remove("is-visible", "is-retreating", "is-startled");
        this.capyPeekButton.hidden = false;
        void this.capyPeekButton.offsetWidth;
        this.capyPeekButton.classList.add("is-visible");

        this.capyPeekRetreatTimerId = window.setTimeout(() => {
            this.capyPeekRetreatTimerId = null;
            this.retreatCapyPeek(PICTIONARY_CAPY_COOLDOWN_MS);
        }, PICTIONARY_CAPY_VISIBLE_MS);
    }

    private retreatCapyPeek(nextDelayMs: number | null) {
        if (this.capyPeekButton.hidden) {
            if (nextDelayMs !== null) {
                this.scheduleCapyPeek(nextDelayMs);
            }
            return;
        }

        if (this.capyPeekRetreatTimerId !== null) {
            window.clearTimeout(this.capyPeekRetreatTimerId);
            this.capyPeekRetreatTimerId = null;
        }

        if (this.capyPeekTransitionTimerId !== null) {
            window.clearTimeout(this.capyPeekTransitionTimerId);
        }

        this.capyPeekButton.classList.remove("is-visible", "is-startled");
        this.capyPeekButton.classList.add("is-retreating");
        this.capyPeekTransitionTimerId = window.setTimeout(() => {
            this.capyPeekTransitionTimerId = null;
            this.hideCapyPeekImmediately();
            if (nextDelayMs !== null) {
                this.scheduleCapyPeek(nextDelayMs);
            }
        }, PICTIONARY_CAPY_RETREAT_MS);
    }

    private reactToCapyPeekTap() {
        if (this.capyPeekButton.hidden) {
            return;
        }

        if (this.capyPeekRetreatTimerId !== null) {
            window.clearTimeout(this.capyPeekRetreatTimerId);
            this.capyPeekRetreatTimerId = null;
        }

        if (this.capyPeekTransitionTimerId !== null) {
            window.clearTimeout(this.capyPeekTransitionTimerId);
        }

        this.capyPeekButton.classList.add("is-startled");
        this.capyPeekTransitionTimerId = window.setTimeout(() => {
            this.capyPeekTransitionTimerId = null;
            this.retreatCapyPeek(
                this.capyPeekAppearances < PICTIONARY_CAPY_MAX_APPEARANCES
                    ? PICTIONARY_CAPY_TAP_RELOCATE_DELAY_MS
                    : null
            );
        }, PICTIONARY_CAPY_TAP_REACTION_MS);
    }

    private syncCapyPeekVisibilityGuard() {
        if (this.capyPeekButton.hidden || this.isCapyPeekAllowed()) {
            return;
        }

        if (this.capyPeekRetreatTimerId !== null) {
            window.clearTimeout(this.capyPeekRetreatTimerId);
            this.capyPeekRetreatTimerId = null;
        }
        if (this.capyPeekTransitionTimerId !== null) {
            window.clearTimeout(this.capyPeekTransitionTimerId);
            this.capyPeekTransitionTimerId = null;
        }
        this.hideCapyPeekImmediately();
        this.scheduleCapyPeek(PICTIONARY_CAPY_GUARD_RETRY_MS);
    }

    private hideCapyPeekImmediately() {
        this.capyPeekButton.hidden = true;
        this.capyPeekButton.classList.remove("is-visible", "is-retreating", "is-startled");
    }

    private clearCapyPeekTimers() {
        if (this.capyPeekScheduleTimerId !== null) {
            window.clearTimeout(this.capyPeekScheduleTimerId);
            this.capyPeekScheduleTimerId = null;
        }
        if (this.capyPeekRetreatTimerId !== null) {
            window.clearTimeout(this.capyPeekRetreatTimerId);
            this.capyPeekRetreatTimerId = null;
        }
        if (this.capyPeekTransitionTimerId !== null) {
            window.clearTimeout(this.capyPeekTransitionTimerId);
            this.capyPeekTransitionTimerId = null;
        }
    }

    private readonly handleDocumentVisibilityChange = () => {
        this.syncCaseTranslations();
        if (document.hidden) {
            this.clearCapyPeekTimers();
            this.hideCapyPeekImmediately();
            return;
        }

        if (this.currentRoom) {
            this.syncCapyPeekSchedule(this.currentRoom);
        }
    };

    private renderViewerChipMarkup(room: RoomView): string {
        return `
            <span class="pictionary-stage-viewer-name">${escapeHtml(room.viewer.name)}</span>
        `;
    }

    private syncGuessSuggestions() {
        const pictionary = this.currentRoom?.pictionary;
        const input = this.consoleSlot.querySelector<HTMLInputElement>("#pictionary-guess-input");
        const field = this.consoleSlot.querySelector<HTMLElement>(".pictionary-guess-field");
        const suggestionsSlot = this.consoleSlot.querySelector<HTMLElement>("[data-pictionary-guess-suggestions]");

        if (!pictionary || !input || !field || !suggestionsSlot) {
            return;
        }

        const hasActiveDraft = !input.disabled && normalizeLooseGuess(this.draftGuess).length > 0;
        const suggestions = hasActiveDraft ? getPictionaryGuessSuggestions(pictionary.wordBank, this.draftGuess) : [];
        const nextSuggestionKey = getPictionaryGuessSuggestionKey(suggestions);

        field.classList.toggle("has-draft", hasActiveDraft);
        field.classList.toggle("has-suggestions", suggestions.length > 0);
        suggestionsSlot.hidden = suggestions.length === 0;

        if (suggestionsSlot.dataset.suggestionKey === nextSuggestionKey) {
            this.syncGuessSuggestionPadding(field, suggestionsSlot);
            return;
        }

        suggestionsSlot.dataset.suggestionKey = nextSuggestionKey;
        suggestionsSlot.innerHTML = renderPictionaryGuessSuggestionButtons(suggestions);
        suggestionsSlot.classList.remove("is-refreshing");
        this.syncGuessSuggestionPadding(field, suggestionsSlot);

        if (suggestions.length > 0) {
            void suggestionsSlot.offsetWidth;
            suggestionsSlot.classList.add("is-refreshing");
        }
    }

    private syncGuessSuggestionPadding(
        field = this.consoleSlot.querySelector<HTMLElement>(".pictionary-guess-field"),
        suggestionsSlot = this.consoleSlot.querySelector<HTMLElement>("[data-pictionary-guess-suggestions]")
    ) {
        if (!field) {
            return;
        }

        if (!suggestionsSlot || suggestionsSlot.hidden) {
            field.style.removeProperty("--pictionary-guess-assist-padding");
            return;
        }

        const assist = suggestionsSlot.closest<HTMLElement>(".pictionary-guess-assist");
        const assistWidth = assist?.getBoundingClientRect().width ?? 0;

        if (assistWidth <= 0) {
            field.style.removeProperty("--pictionary-guess-assist-padding");
            return;
        }

        field.style.setProperty("--pictionary-guess-assist-padding", `${Math.ceil(assistWidth + 12)}px`);
    }

    private renderConsoleMarkup(room: RoomView, role: RenderRole): string {
        const pictionary = room.pictionary;

        if (!pictionary || role === "controller") {
            return "";
        }

        if (role === "host" || role === "host-controller") {
            return "";
        }

        const viewer = pictionary.viewer;
        const feedbackMessage = this.getPlayerFeedbackMessage(room);
        const feedbackToneClass = getFeedbackToneClass(viewer?.lastFeedbackKind);
        const showGuessControls = Boolean(
            pictionary.roundActive &&
                !pictionary.roundComplete &&
                viewer &&
                !viewer.solved
        );
        const viewerCanGuessNow = this.canViewerGuessNow(pictionary);
        const currentRiskCount = this.getCurrentGuessRiskCount(room);
        const tryLossAnimation = this.getActiveTryLossAnimation();
        const displayedRiskCount = currentRiskCount > 0 ? currentRiskCount : tryLossAnimation?.lostCount ?? 0;
        const livesMarkup = buildLivesMarkup(viewer?.livesRemaining ?? 0, currentRiskCount, tryLossAnimation);
        const hasGuessDraft = viewerCanGuessNow && normalizeLooseGuess(this.draftGuess).length > 0;
        const adviceMessage =
            showGuessControls && viewer?.breakthroughBonusAvailable
                ? viewer.breakthroughEffortActive
                    ? t("breakthroughEffortAdvice")
                    : t("breakthroughBonusAdvice")
                : "";
        const guessSuggestions = viewerCanGuessNow
            ? getPictionaryGuessSuggestions(pictionary.wordBank, this.draftGuess)
            : [];
        const guessFieldStateClass = [
            feedbackToneClass,
            hasGuessDraft ? "has-draft" : "",
            guessSuggestions.length > 0 ? "has-suggestions" : ""
        ]
            .filter(Boolean)
            .join(" ");
        const guessSuggestionKey = getPictionaryGuessSuggestionKey(guessSuggestions);
        const consoleStateClass = showGuessControls
            ? "has-guess-controls"
            : feedbackMessage
              ? "is-feedback-only"
              : "";

        return `
            <div class="pictionary-console pictionary-console--player ${feedbackToneClass} ${consoleStateClass}">
                <div class="pictionary-console-topline">
                    <div class="pictionary-console-metrics">
                        ${
                            showGuessControls
                                ? `
                                    <span class="pictionary-inline-chip pictionary-inline-chip--lives">
                                        <span class="pictionary-inline-label">${escapeHtml(t("lives"))}</span>
                                        <span class="pictionary-life-strip" aria-label="${escapeHtml(
                                            formatLivesRemaining(viewer?.livesRemaining ?? 0)
                                        )}">${livesMarkup}</span>
                                        <span
                                            class="pictionary-life-risk${displayedRiskCount > 0 ? "" : " is-empty"}"
                                            data-guess-risk
                                            aria-hidden="${displayedRiskCount > 0 ? "false" : "true"}"
                                        >${displayedRiskCount > 0 ? escapeHtml(`-${displayedRiskCount}`) : ""}</span>
                                    </span>
                                    <span class="pictionary-inline-chip pictionary-inline-chip--worth">
                                        <span class="pictionary-inline-label">${escapeHtml(t("worth"))}</span>
                                        <strong>${escapeHtml(formatPts(pictionary.currentWorth))}</strong>
                                    </span>
                                `
                                : ""
                        }
                    </div>
                    <div class="pictionary-console-actions">
                        <span class="pictionary-console-timer" data-viewer-cooldown hidden></span>
                    </div>
                </div>
                ${
                    adviceMessage
                        ? `<p class="pictionary-advice-message">${escapeHtml(adviceMessage)}</p>`
                        : ""
                }
                ${
                    feedbackMessage
                        ? `<p class="pictionary-feedback-message ${feedbackToneClass}" role="status" aria-live="polite">${escapeHtml(
                              feedbackMessage
                          )}</p>`
                        : ""
                }
                ${
                    showGuessControls
                        ? `
                            <div class="pictionary-guess-row">
                                <div class="field pictionary-guess-field ${guessFieldStateClass}">
                                    <div class="pictionary-guess-shell">
                                        <input
                                            id="pictionary-guess-input"
                                            type="text"
                                            autocomplete="off"
                                            aria-label="${escapeHtml(t("guessWord"))}"
                                            placeholder="${escapeHtml(this.getGuessPlaceholder(pictionary))}"
                                            value="${escapeHtml(this.draftGuess)}"
                                            ${viewerCanGuessNow ? "" : "disabled"}
                                        />
                                        <div class="pictionary-guess-assist">
                                            <div
                                                class="pictionary-guess-suggestions"
                                                data-pictionary-guess-suggestions
                                                data-suggestion-key="${escapeHtml(guessSuggestionKey)}"
                                                ${guessSuggestions.length > 0 ? "" : "hidden"}
                                            >${renderPictionaryGuessSuggestionButtons(guessSuggestions)}</div>
                                            <span
                                                class="pictionary-guess-search-zone"
                                                data-action="focus-guess-input"
                                                aria-hidden="true"
                                            >
                                                ${GUESS_MAGNIFIER_ICON}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    class="primary-button pictionary-guess-button"
                                    type="button"
                                    data-action="guess-submit"
                                    data-compact-label="→"
                                    ${viewerCanGuessNow ? "" : "disabled"}
                                >${escapeHtml(
                                    this.getViewerObserveCountdown(pictionary)
                                        ? t("observeCountdown", {
                                              seconds: this.getViewerObserveCountdown(pictionary)
                                          })
                                        : t("guess")
                                )}</button>
                            </div>
                        `
                        : ""
                }
            </div>
        `;
    }

    private renderControllerRoomSwitchMarkup(roomCode: string): string {
        const normalizedRoomCode = formatControllerRoomCode(roomCode);

        return `
            <div class="pictionary-controller-room-switch">
                <input
                    id="pictionary-controller-room-code-input"
                    class="pictionary-controller-room-code-input"
                    type="text"
                    value="${escapeHtml(normalizedRoomCode)}"
                    maxlength="${ROOM_CODE_MAX_LENGTH}"
                    autocomplete="off"
                    spellcheck="false"
                    inputmode="text"
                    aria-label="${escapeHtml(t("controllerRoomCode"))}"
                    title="${escapeHtml(t("controllerRoomCode"))}"
                />
                <button
                    class="secondary-button pictionary-controller-room-switch-button"
                    type="button"
                    data-action="switch-controller-room"
                    aria-label="${escapeHtml(t("switchRoom"))}"
                    title="${escapeHtml(t("switchRoom"))}"
                >${escapeHtml(t("switchRoomShort"))}</button>
            </div>
        `;
    }

    private renderRoleMarkup(room: RoomView, role: RenderRole): string {
        const pictionary = room.pictionary;

        if (!pictionary || role === "host" || role === "player") {
            return "";
        }

        if (!room.viewer.isController) {
            return `
                <div class="pictionary-controller-lock-copy">
                    <h2 class="pictionary-role-title">${escapeHtml(
                        t("controllerLocked")
                    )}</h2>
                    <p class="pictionary-role-status">${escapeHtml(
                        pictionary.controllerName
                            ? t("controllerLockedTo", { name: pictionary.controllerName })
                            : t("waitingForControllerSeat")
                    )}</p>
                    <p class="pictionary-role-meta" data-controller-release-countdown hidden></p>
                </div>
            `;
        }

        const selectionOpen = this.controllerSelectionOpen || !pictionary.roundActive || pictionary.roundComplete;
        const selectedEntry = pictionary.entries.find((entry) => entry.index === this.selectedEntryIndex) ?? null;
        const liveEntry =
            pictionary.currentWordIndex !== null
                ? pictionary.entries.find((entry) => entry.index === pictionary.currentWordIndex) ?? null
                : null;
        const previewEntry = selectionOpen
            ? selectedEntry ?? (pictionary.roundComplete ? null : liveEntry)
            : liveEntry ?? selectedEntry;
        const hasActiveSolvers = pictionary.roundActive && !pictionary.roundComplete;
        const guessersRemaining = hasActiveSolvers ? this.getGuessersRemainingCount(pictionary) : 0;
        const pickerPanelClass = selectionOpen ? " is-next-action" : "";
        const displayedEntries = [...pictionary.entries].sort(comparePictionaryDisplayEntries);
        if (pictionary.vocabularyReview?.open && role === "controller" && !hasActiveSolvers) {
            return `<div class="pictionary-review-remote">${this.renderWordBankMarkup(room)}</div>`;
        }
        return `
            <div class="pictionary-controller-shell ${role === "host-controller" ? "is-host-controller" : ""}">
                <div class="pictionary-controller-topline">
                    <div class="pictionary-controller-stat-row">
                        <div class="pictionary-controller-stat-chip pictionary-controller-round-chip">
                            <span class="pictionary-controller-round-label">${escapeHtml(t("round"))}</span>
                            <strong class="pictionary-controller-round-value">${escapeHtml(
                                String(pictionary.roundNumber)
                            )}</strong>
                        </div>
                        <div class="pictionary-controller-stat-chip pictionary-controller-remaining-chip ${
                            hasActiveSolvers ? "" : "is-idle"
                        }">
                            <span class="pictionary-stepper-label">${escapeHtml(t("stillGuessing"))}</span>
                            <strong>${escapeHtml(String(guessersRemaining))}</strong>
                        </div>
                        <button class="secondary-button pictionary-review-launch" type="button" data-action="show-vocabulary-review"
                            ${hasActiveSolvers ? "disabled" : ""}>${escapeHtml(t("reviewVocabulary"))}</button>
                    </div>
                    <div class="pictionary-controller-control-grid">
                        ${this.renderWorthControlMarkup({
                            label: t("worth"),
                            value: formatPts(this.selectedPointValue),
                            decrementDisabled: this.selectedPointValue <= PICTIONARY_MIN_POINT_VALUE,
                            incrementDisabled: this.selectedPointValue >= PICTIONARY_MAX_POINT_VALUE,
                            autoSelected: !this.pointValueDirty
                        })}
                        ${this.renderHintControlMarkup({
                            label: t("hintReveal"),
                            value: `${this.selectedRevealDurationSeconds}s`,
                            decrementAction: "decrement-reveal-duration",
                            incrementAction: "increment-reveal-duration",
                            hintMode: this.selectedHintMode
                        })}
                    </div>
                </div>
                <div class="pictionary-controller-picker-panel${pickerPanelClass}">
                    ${
                        previewEntry
                            ? this.renderControllerWordCard(
                                  previewEntry.english,
                                  previewEntry.korean,
                                  previewEntry.stem,
                                  !selectionOpen
                              )
                            : `
                                <div class="pictionary-controller-current is-empty">
                                    <span class="pictionary-controller-placeholder">${escapeHtml(t("pickWord"))}</span>
                                </div>
                            `
                    }
                    <div class="pictionary-controller-actions">
                        ${
                            selectionOpen
                                ? `
                                    <button
                                        class="primary-button pictionary-next-button"
                                        type="button"
                                        data-action="next-round"
                                        ${this.selectedEntryIndex === null ? "disabled" : ""}
                                    >${escapeHtml(
                                        !pictionary.roundActive
                                            ? t("start")
                                            : pictionary.roundComplete
                                              ? t("nextWord")
                                              : t("endRoundNextWord")
                                    )}</button>
                                    ${
                                        pictionary.roundActive && !pictionary.roundComplete
                                            ? `<button
                                                    class="secondary-button pictionary-controller-close-button"
                                                    type="button"
                                                    data-action="close-controller-picker"
                                               >${escapeHtml(t("close"))}</button>`
                                            : ""
                                    }
                                `
                                : `
                                    <button
                                        class="primary-button pictionary-next-button"
                                        type="button"
                                        data-action="next-round"
                                        ${!pictionary.roundActive && this.selectedEntryIndex === null ? "disabled" : ""}
                                    >${escapeHtml(
                                        !pictionary.roundActive
                                            ? t("start")
                                            : pictionary.roundComplete
                                              ? t("pickWord")
                                              : t("endRoundNextWord")
                                    )}</button>
                                `
                        }
                    </div>
                    ${
                        selectionOpen
                            ? `
                                <div class="pictionary-entry-grid">
                                    ${displayedEntries
                                        .map(
                                            (entry) => `
                                                <button
                                                    class="pictionary-entry-button ${
                                                        this.selectedEntryIndex === entry.index ? "is-selected" : ""
                                                    }${entry.timesUsed > 0 ? " has-used-count" : ""}"
                                                    type="button"
                                                    data-entry-index="${entry.index}"
                                                    aria-pressed="${
                                                        this.selectedEntryIndex === entry.index ? "true" : "false"
                                                    }"
                                                >
                                                    <span class="pictionary-entry-english">${escapeHtml(entry.english)}</span>
                                                    ${
                                                        pictionary.noTranslations || !entry.korean
                                                            ? ""
                                                            : `<span class="pictionary-entry-korean">${escapeHtml(
                                                                  entry.korean
                                                              )}</span>`
                                                    }
                                                    ${
                                                        entry.timesUsed > 0
                                                            ? `<span class="pictionary-entry-used">${escapeHtml(
                                                                  t("usedCount", { count: entry.timesUsed })
                                                              )}</span>`
                                                            : ""
                                                    }
                                                </button>
                                            `
                                        )
                                        .join("")}
                                </div>
                            `
                            : ""
                    }
                </div>
            </div>
        `;
    }

    private renderWorthControlMarkup(options: {
        label: string;
        value: string;
        decrementDisabled?: boolean;
        incrementDisabled?: boolean;
        autoSelected: boolean;
    }): string {
        const modeOptions: Array<{ mode: "auto" | "manual"; label: string }> = [
            { mode: "auto", label: t("auto") },
            { mode: "manual", label: t("manual") }
        ];

        return `
            <div class="pictionary-stepper-card pictionary-stepper-card--worth">
                <span class="pictionary-stepper-label">${escapeHtml(options.label)}</span>
                <div class="pictionary-stepper-row">
                    <button
                        class="pictionary-stepper-button"
                        type="button"
                        data-action="decrement-points"
                        aria-label="${escapeHtml(`${options.label} -`)}"
                        ${options.decrementDisabled ? "disabled" : ""}
                    >-</button>
                    <strong class="pictionary-stepper-value">${escapeHtml(options.value)}</strong>
                    <button
                        class="pictionary-stepper-button"
                        type="button"
                        data-action="increment-points"
                        aria-label="${escapeHtml(`${options.label} +`)}"
                        ${options.incrementDisabled ? "disabled" : ""}
                    >+</button>
                </div>
                <div class="pictionary-segmented-control pictionary-worth-mode-control" role="group" aria-label="${escapeHtml(
                    options.label
                )}">
                    ${modeOptions
                        .map(
                            ({ mode, label }) => `
                                <button
                                    class="pictionary-segmented-button ${
                                        options.autoSelected === (mode === "auto") ? "is-selected" : ""
                                    }"
                                    type="button"
                                    data-action="set-worth-mode"
                                    data-worth-mode="${mode}"
                                    aria-pressed="${
                                        options.autoSelected === (mode === "auto") ? "true" : "false"
                                    }"
                                >${escapeHtml(label)}</button>
                            `
                        )
                        .join("")}
                </div>
            </div>
        `;
    }

    private getGuessersRemainingCount(pictionary: NonNullable<RoomView["pictionary"]>): number {
        if (!pictionary.roundActive || pictionary.roundComplete) {
            return pictionary.teams.filter((team) => team.connected && !team.isAway).length;
        }

        return pictionary.teams.filter(
            (team) => team.connected && !team.isAway && !team.solved
        ).length;
    }

    private renderHintControlMarkup(options: {
        label: string;
        value: string;
        decrementAction: string;
        incrementAction: string;
        hintMode: PictionaryHintMode;
    }): string {
        const hintOptions: Array<{ mode: PictionaryHintMode; label: string; shortLabel: string }> = [
            { mode: "over-time", label: t("overTime"), shortLabel: t("overTimeShort") },
            { mode: "all-at-once", label: t("allAtOnce"), shortLabel: t("allAtOnceShort") },
            { mode: "off", label: t("off"), shortLabel: t("off") }
        ];
        const timerDisabled = options.hintMode === "off";

        return `
            <div class="pictionary-stepper-card pictionary-stepper-card--hint">
                <span class="pictionary-stepper-label">${escapeHtml(options.label)}</span>
                <div class="pictionary-stepper-row ${timerDisabled ? "is-disabled" : ""}">
                    <button
                        class="pictionary-stepper-button"
                        type="button"
                        data-action="${escapeHtml(options.decrementAction)}"
                        aria-label="${escapeHtml(`${options.label} -`)}"
                        ${timerDisabled ? "disabled" : ""}
                    >-</button>
                    <strong class="pictionary-stepper-value">${escapeHtml(options.value)}</strong>
                    <button
                        class="pictionary-stepper-button"
                        type="button"
                        data-action="${escapeHtml(options.incrementAction)}"
                        aria-label="${escapeHtml(`${options.label} +`)}"
                        ${timerDisabled ? "disabled" : ""}
                    >+</button>
                </div>
                <div class="pictionary-segmented-control pictionary-hint-mode-control" role="group" aria-label="${escapeHtml(
                    options.label
                )}">
                    ${hintOptions
                        .map(
                            ({ mode, label, shortLabel }) => `
                                <button
                                    class="pictionary-segmented-button ${options.hintMode === mode ? "is-selected" : ""}"
                                    type="button"
                                    data-action="set-hint-mode"
                                    data-hint-mode="${mode}"
                                    aria-pressed="${options.hintMode === mode ? "true" : "false"}"
                                    aria-label="${escapeHtml(label)}"
                                    title="${escapeHtml(label)}"
                                >
                                    <span class="pictionary-segmented-label pictionary-segmented-label--full" aria-hidden="true">${escapeHtml(
                                        label
                                    )}</span>
                                    <span class="pictionary-segmented-label pictionary-segmented-label--short" aria-hidden="true">${escapeHtml(
                                        shortLabel
                                    )}</span>
                                </button>
                            `
                        )
                        .join("")}
                </div>
            </div>
        `;
    }

    private renderControllerWordCard(english: string, korean: string, stem: string, isPrivate: boolean): string {
        const content = `
            <span class="pictionary-controller-private-content">
                <span class="pictionary-controller-wordline">
                    <strong class="pictionary-controller-word">${escapeHtml(english)}</strong>
                    ${
                        korean
                            ? `<strong class="pictionary-controller-word is-translation">${escapeHtml(korean)}</strong>`
                            : ""
                    }
                </span>
                ${stem ? `<span class="pictionary-controller-hint">${escapeHtml(stem)}</span>` : ""}
            </span>
        `;

        if (!isPrivate) {
            return `
                <div class="pictionary-controller-current is-highlighted is-preview">
                    ${content}
                </div>
            `;
        }

        return `
            <button
                class="pictionary-controller-current is-highlighted is-private"
                type="button"
                aria-label="${escapeHtml(t("reveal"))}"
                title="${escapeHtml(t("reveal"))}"
            >
                <span class="pictionary-controller-reveal-cue" aria-hidden="true">
                    ${PRIVATE_WORD_ICON}
                    <span>${escapeHtml(t("reveal"))}</span>
                </span>
                ${content}
            </button>
        `;
    }

    private renderHostUtilityMarkup(room: RoomView): string {
        const pictionary = room.pictionary;

        if (!pictionary || (this.currentRole !== "host" && this.currentRole !== "host-controller")) {
            return "";
        }

        return renderActiveHostJoinUtility({
            roomCode: room.roomCode,
            controllerConnected: pictionary.controllerIsHost || pictionary.controllerConnected,
            rootClassName: "pictionary-host-utility-strip",
            actionsClassName: "pictionary-host-utility-actions",
            controllerButtonClassName: "pictionary-host-controller-button"
        });
    }

    private renderHostRosterMarkup(room: RoomView): string {
        if (!room.pictionary || (this.currentRole !== "host" && this.currentRole !== "host-controller")) {
            return "";
        }

        const teamBySessionId = new Map(room.pictionary.teams.map((team) => [team.sessionId, team]));
        const participants = room.participants
            .filter((participant) => participant.sessionId !== room.viewer.sessionId && !participant.isController)
            .slice()
            .sort((left, right) => {
                const leftTeam = teamBySessionId.get(left.sessionId);
                const rightTeam = teamBySessionId.get(right.sessionId);

                if (leftTeam && rightTeam && leftTeam.score !== rightTeam.score) {
                    return rightTeam.score - leftTeam.score;
                }

                if (leftTeam && !rightTeam) {
                    return -1;
                }

                if (!leftTeam && rightTeam) {
                    return 1;
                }

                return left.name.localeCompare(right.name);
            });
        const placementBySessionId = assignOrderedPlayerRosterPlacements(
            participants,
            (participant) => (teamBySessionId.get(participant.sessionId)?.score ?? 0) > 0
        );
        const playerCount = participants.length;

        return `
            <div class="roster-head">
                ${renderPlayerHistoryHeaderMenu(room, String(playerCount), {
                    open: this.playerHistoryMenuOpen
                })}
            </div>
            <div class="roster-body">
                ${
                    participants.length === 0
                        ? `<p class="roster-empty">${escapeHtml(t("noPlayersYet"))}</p>`
                        : `
                            <ul class="${getPlayerRosterListClass("roster-list", participants.length)}">
                                ${participants
                                    .map((participant) => {
                                        const team = teamBySessionId.get(participant.sessionId);
                                        const awayReason =
                                            participant.awayReason ??
                                            team?.awayReason ??
                                            (!participant.connected ? "disconnected" : null);
                                        const isSolved = Boolean(team?.solved);
                                        const isAway = !isSolved &&
                                            (participant.isAway || !participant.connected || Boolean(team?.isAway));
                                        const renameButtonId = buildPictionaryRosterRenameButtonId(participant.sessionId);
                                        const actionsKey = buildPictionaryRosterActionsKey(participant.sessionId);
                                        const displayName = getPlayerRosterDisplayName(
                                            participant.name,
                                            participant.renamePending,
                                            t("renamePendingName")
                                        );
                                        const placement = placementBySessionId.get(participant.sessionId) ?? null;
                                        const livesRemaining = team?.livesRemaining ?? PICTIONARY_LIVES_PER_ROUND;
                                        const score = team?.score ?? 0;
                                        const scorePopup = this.renderTeamScorePopupMarkup(participant.sessionId);
                                        const actionMenu = renderPlayerRosterActionsMenu(
                                            `${t("playerActions")}: ${displayName}`,
                                            [
                                                {
                                                    attributes: {
                                                        id: renameButtonId,
                                                        "data-action": "rename-roster-participant",
                                                        "data-session-id": participant.sessionId,
                                                        "aria-label": `${t("editName")}: ${displayName}`,
                                                        title: `${t("editName")}: ${displayName}`
                                                    },
                                                    className: "participant-rename-button pictionary-roster-rename-button",
                                                    label: t("editName")
                                                },
                                                {
                                                    attributes: {
                                                        "data-action": "prompt-rename-roster-participant",
                                                        "data-session-id": participant.sessionId,
                                                        "aria-label": `${t("askRename")}: ${displayName}`,
                                                        title: `${t("askRename")}: ${displayName}`
                                                    },
                                                    className: "participant-rename-prompt-button pictionary-roster-rename-prompt-button",
                                                    label: t("askRename")
                                                },
                                                {
                                                    attributes: {
                                                        "data-action": "kick-roster-participant",
                                                        "data-session-id": participant.sessionId,
                                                        "aria-label": `${t("kick")}: ${displayName}`,
                                                        title: `${t("kick")}: ${displayName}`
                                                    },
                                                    className: "participant-kick-button pictionary-roster-kick-button",
                                                    label: t("kick")
                                                }
                                            ],
                                            {
                                                align: "start",
                                                key: actionsKey,
                                                open: this.openRosterActionsKey === actionsKey,
                                                scope: "pictionary",
                                                triggerId: buildPictionaryRosterActionsTriggerId(participant.sessionId),
                                                triggerClassName: "pictionary-roster-name-trigger",
                                                triggerLabel: displayName
                                            }
                                        );

                                        return `
                                            <li class="roster-row ${isAway ? "is-away" : ""} ${isSolved ? "is-solved" : ""}${getPlayerRosterPlacementClass(
                                                placement
                                            )}${getPlayerRenamePendingClass(participant.renamePending)}">
                                                <div class="pictionary-roster-copy">
                                                    <div class="pictionary-roster-name-row">
                                                        ${actionMenu}
                                                        ${scorePopup}
                                                        ${
                                                            isAway
                                                                ? `<span class="pictionary-roster-away-badge" data-away-reason="${escapeHtml(
                                                                      awayReason ?? "away"
                                                                  )}" title="${escapeHtml(
                                                                      t("away")
                                                                  )}" aria-label="${escapeHtml(t("away"))}">${escapeHtml(
                                                                      t("away")
                                                                  )}</span>`
                                                                : ""
                                                        }
                                                    </div>
                                                </div>
                                                ${renderPictionaryCardMetricsMarkup(
                                                    livesRemaining,
                                                    score,
                                                    "pictionary-roster-metrics",
                                                    {
                                                        adjustableSessionId: participant.sessionId,
                                                        adjustOpen: this.scoreAdjustSessionId === participant.sessionId
                                                    }
                                                )}
                                            </li>
                                        `;
                                    })
                                    .join("")}
                            </ul>
                        `
                }
            </div>
        `;
    }

    private renderWordBankMarkup(room: RoomView): string {
        const pictionary = room.pictionary;

        if (!pictionary || pictionary.wordBank.length === 0) {
            return "";
        }

        const displayedWordBank = [...pictionary.wordBank].sort(comparePictionaryDisplayEntries);
        const showTranslations =
            !pictionary.noTranslations && displayedWordBank.some((entry) => entry.korean.trim().length > 0);

        return `
            <div class="pictionary-word-bank-head">
                <span class="card-label">${escapeHtml(t("wordBank"))} <span>${displayedWordBank.length}</span></span>
                ${this.reviewAppearance.renderButton()}
                <button
                    class="secondary-button pictionary-scoreboard-close"
                    type="button"
                    data-action="close-word-bank"
                >${escapeHtml(t("returnToGame"))}</button>
            </div>
            ${renderReviewNavigation(pictionary.vocabularyReview?.index ?? 0, displayedWordBank.length, true)}
            ${renderReviewList({ entries: displayedWordBank, index: pictionary.vocabularyReview?.index ?? 0, showTranslations, pictionary: true })}
        `;
    }

    private renderPlayerWordBankTrayMarkup(pictionary: NonNullable<RoomView["pictionary"]>): string {
        if (pictionary.wordBank.length === 0) {
            return "";
        }

        const displayedWordBank = [...pictionary.wordBank].sort(comparePictionaryDisplayEntries);
        const showTranslations =
            !pictionary.noTranslations && displayedWordBank.some((entry) => entry.korean.trim().length > 0);

        return `
            <section class="pictionary-player-word-bank" aria-label="${escapeHtml(t("wordBank"))}">
                <div class="pictionary-player-word-bank-head">
                    <button class="pictionary-case-file-toggle" type="button" data-action="toggle-word-bank" aria-expanded="${this.wordBankOpen}"
                        aria-label="${escapeHtml(`${t(this.wordBankOpen ? "close" : "expandCaseFile")}: ${displayedWordBank.length}`)}">
                        <span class="pictionary-player-word-bank-count" aria-hidden="true">${escapeHtml(String(displayedWordBank.length))}</span>
                        <span>${escapeHtml(t("wordBank"))}</span>
                    </button>
                    ${showTranslations ? `<button type="button" class="pictionary-translation-button"
                        data-action="reveal-case-translations" data-phase="ready" aria-label="${escapeHtml(t("revealCaseTranslations"))}">
                        <span class="pictionary-translation-dial" aria-hidden="true">
                            <svg viewBox="0 0 32 32" class="pictionary-translation-ring">
                                <circle cx="16" cy="16" r="14" class="pictionary-translation-ring-track" />
                                <circle cx="16" cy="16" r="14" pathLength="100" stroke-dasharray="100"
                                    stroke-dashoffset="0" class="pictionary-translation-ring-progress" />
                            </svg>${renderZiarcadiaIcon("translate")}
                        </span>
                        <span>${escapeHtml(t("revealCaseTranslations"))}</span>
                    </button>` : ""}
                </div>
                <div class="pictionary-player-word-bank-list" tabindex="0" role="region" aria-label="${escapeHtml(t("wordBank"))}">
                    ${displayedWordBank
                        .map(
                            (entry) => `
                                <span class="pictionary-player-word-bank-chip">
                                    <strong>${escapeHtml(entry.english)}</strong>
                                    ${
                                        showTranslations
                                            ? `<span class="pictionary-case-translation" lang="ko" aria-hidden="true">${entry.korean.trim().length > 0 ? escapeHtml(entry.korean) : ""}</span>`
                                            : ""
                                    }
                                </span>
                            `
                        )
                        .join("")}
                </div>
            </section>
        `;
    }

    private renderPlayerBoardMarkup(room: RoomView): string {
        const pictionary = room.pictionary;

        if (!pictionary) {
            return "";
        }

        const wordBankMarkup = this.renderPlayerWordBankTrayMarkup(pictionary);

        if (!wordBankMarkup) {
            return "";
        }

        const layoutClasses = [
            "pictionary-player-board-layout",
            "has-word-bank"
        ]
            .filter(Boolean)
            .join(" ");

        return `
            <div class="${layoutClasses}">
                ${wordBankMarkup}
            </div>
        `;
    }

    private renderScoreboardMarkup(room: RoomView): string {
        const pictionary = room.pictionary;

        if (!pictionary || (this.currentRole !== "host" && this.currentRole !== "host-controller")) {
            return "";
        }

        if (pictionary.teams.length === 0) {
            return `
                <div class="pictionary-scoreboard-head">
                    <button
                        class="secondary-button pictionary-scoreboard-close"
                        type="button"
                        data-action="close-team-board"
                    >${escapeHtml(t("close"))}</button>
                </div>
                <p class="pictionary-scoreboard-empty">${escapeHtml(t("noTeamsYet"))}</p>
            `;
        }

        return `
            <div class="pictionary-scoreboard-head">
                <button
                    class="secondary-button pictionary-scoreboard-close"
                    type="button"
                    data-action="close-team-board"
                >${escapeHtml(t("close"))}</button>
            </div>
            <ul class="pictionary-team-list">
                ${pictionary.teams
                    .slice()
                    .sort((left, right) => {
                        if (right.score !== left.score) {
                            return right.score - left.score;
                        }

                        return left.name.localeCompare(right.name);
                    })
                    .map(
                        (team) => {
                            const scorePopup = this.renderTeamScorePopupMarkup(team.sessionId);

                            return `
                                <li class="pictionary-team-row ${team.solved ? "is-solved" : ""}">
                                    <div class="pictionary-team-copy">
                                        <span class="pictionary-team-name-line">
                                            <strong class="pictionary-team-name">${escapeHtml(team.name)}</strong>
                                            ${scorePopup}
                                        </span>
                                        <span class="pictionary-team-meta">${escapeHtml(
                                            team.solved && team.solvedRank !== null
                                                ? t("solvedRank", { rank: team.solvedRank })
                                                : !team.isAway
                                                  ? formatLivesRemaining(team.livesRemaining)
                                                  : t("away")
                                        )}</span>
                                    </div>
                                    <strong class="pictionary-team-score">${escapeHtml(formatPts(team.score))}</strong>
                                </li>
                            `;
                        }
                    )
                    .join("")}
            </ul>
        `;
    }

    private getPlayerFeedbackMessage(room: RoomView): string {
        const pictionary = room.pictionary;
        const viewer = pictionary?.viewer;

        if (!pictionary || !viewer) {
            return "";
        }

        if (viewer.lastFeedback) {
            return localizeStatus(viewer.lastFeedback);
        }

        if (
            !pictionary.roundActive ||
            pictionary.roundComplete ||
            viewer.solved ||
            Boolean(viewer.cooldownEndsAt && viewer.cooldownEndsAt > this.getServerAlignedNow())
        ) {
            if (!pictionary.roundActive || pictionary.roundComplete) {
                return getPictionaryWaitingPrompt(pictionary);
            }

            return isDefaultGuessPrompt(room.statusMessage) ? "" : localizeStatus(room.statusMessage);
        }

        return "";
    }
}

export const createPictionaryView = (root: HTMLElement, options: PictionaryViewOptions) =>
    new PictionaryView(root, options);
